import {
  Badge,
  Caption1,
  Checkbox,
  Menu,
  MenuItem,
  MenuItemRadio,
  MenuList,
  MenuPopover,
  MenuTrigger,
  makeStyles,
  mergeClasses,
  tokens,
} from "@fluentui/react-components";
import {
  Add16Regular,
  ArrowCounterclockwise16Regular,
  ArrowDown16Regular,
  ArrowLeft16Regular,
  ArrowRight16Regular,
  ArrowUp16Regular,
  CalendarClock16Regular,
  CalendarLtr16Regular,
  ChevronDown12Regular,
  ClipboardTaskList16Regular,
  Dismiss12Regular,
  Document16Regular,
  Delete16Regular,
  Edit16Regular,
  Eye16Regular,
  History16Regular,
  Mail16Regular,
  MoreHorizontal16Regular,
  Print16Regular,
  ReOrderDotsVertical16Regular,
  Warning16Regular,
  type FluentIcon,
} from "@fluentui/react-icons";
import { useEffect, useMemo, useRef, useState, type ComponentProps, type KeyboardEvent, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { PlanAssigneeWeekly, WeeklyScopeSwitch } from "@/features/user-weekly/UserWeeklyPanel";
import { AppDialog, ConfirmDialog, DialogActionsBar, DialogCheckbox, DialogField } from "@/components/app-dialog";
import { FuiButton, FuiDropdown, FuiInput, FuiSearchBox } from "@/components/fui-control";
import { ListToolbar } from "@/components/list-toolbar";
import { PageHeaderTitle } from "@/components/ui-typography";
import { ProgressDelta, StatusWord } from "@/components/visual/ink-report";
import { saveBytes } from "@/desktop/file-io";
import { addDays, isISODate, type ISODate } from "@/engine/date";
import type {
  RecordSeverity,
  WeeklyAgendaLine,
  WeeklyIssueLine,
  WeeklyLineOrigin,
  WeeklyReportDraft,
  WeeklyRequestLine,
  WeeklySectionVisibility,
  WeeklyTaskLine,
  WeeklyWbsLine,
} from "@/engine/types";
import { useCan } from "@/features/access/use-plan-access";
import { COST_KPI, UNAVAILABLE_COST, costKpiText } from "@/features/pm/cost-evm";
import { severityGlyph } from "@/features/records/record-glyphs";
import { SeverityBadge } from "@/features/records/record-ui";
import { suggestPeriodName } from "@/features/reports/report-model";
import { formatDate } from "@/lib/date-format";
import { SegmentedControl } from "@/features/resources/SegmentedControl";
import weeklyFontHref from "@/features/weekly-report/fonts/noto-sans-kr-text.ttf" with { type: "file" };
import {
  InlineDatePopover,
  InlineGhostRow,
  InlineMemberPopover,
  InlinePopoverBadge,
  InlineText,
  SuggestionDrawer,
} from "@/features/weekly-report/components";
import { newId } from "@/store/tree-ops";
import {
  addManualDraftLine,
  applyCarryOverToDraft,
  buildReportFromDraft,
  buildWeeklyHistoryFromProject,
  collectCarryOverCandidates,
  collectPastItems,
  copyPastItemToDraft,
  DEFAULT_WEEKLY_SECTIONS,
  deleteDraftLine,
  excludeDraftLine,
  findPreviousClosedSnapshot,
  includeDraftLine,
  listWeeklyEntries,
  moveDraftLine,
  pmPersonOptions,
  promoteManualIssueToRecord,
  reloadDraftProgress,
  reloadDraftRecords,
  reloadDraftSchedule,
  reloadSingleDraftLine,
  reorderDraftLines,
  seedWeeklyDraft,
  seedWeekMemo,
  shortWeekLabel,
  updateDraftAgendaLine,
  updateDraftIssueLine,
  updateDraftRequestLine,
  updateDraftTaskLine,
  type WeeklyEntryMeta,
  type WeeklyListKey,
} from "@/features/weekly-report/weekly-draft";
import { buildWeeklyPdf } from "@/features/weekly-report/weekly-pdf";
import { setWeeklyMockCase, useWeeklyMockCase, WEEKLY_MOCK_CASES, weeklyProjectForMock } from "@/features/weekly-report/weekly-mock";
import type { WeeklyV2IssueItem, WeeklyV2WeekSnapshot, WeeklyV2WbsRow } from "@/features/weekly-report/weekly-report-v2-data";
import { findWeeklySnapshot, findWeeklySnapshotsForDate, readFrozenReport } from "@/features/weekly-report/weekly-snapshot";
import { useDialogLayer } from "@/components/ui/dialog-layer";
import { contentShell, listBody, shellGutter } from "@/shell/content-frame";
import { useCatalogStore } from "@/store/catalog-store";
import { useProjectStore } from "@/store/project-store";
import { recordCode } from "@/store/record-ops";

/** 시트 안의 편집 동작. 초안을 고치는 핸들러는 그대로 두고, 버튼만 공용 컨트롤을 쓴다. */
function SheetButton({ active = false, ...props }: ComponentProps<typeof FuiButton> & { active?: boolean }) {
  return <FuiButton appearance={active ? "primary" : "subtle"} {...props} />;
}

function MetaBadge({
  children,
  danger = false,
  title,
  lead = false,
}: {
  children: ReactNode;
  danger?: boolean;
  title?: string;
  lead?: boolean;
}) {
  return (
    <Badge
      appearance="tint"
      color={danger ? "danger" : "subtle"}
      size="small"
      title={title}
      style={lead ? { marginRight: 6 } : { marginLeft: 6 }}
    >
      {children}
    </Badge>
  );
}

function formatTimeOnly(isoString?: string): string {
  if (!isoString) return "";
  try {
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return "";
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  } catch {
    return "";
  }
}

function weeklyFontRequest(): string {
  if (weeklyFontHref.startsWith("/") || weeklyFontHref.startsWith("http")) return weeklyFontHref;
  if (typeof window === "undefined") return weeklyFontHref;
  return new URL(weeklyFontHref.replace(/^\.\//, ""), `${window.location.origin}/`).toString();
}

/** 주간보고 시트 전용. PDF와 같은 본문 글꼴이고, 화면 크롬의 글꼴 스택과 섞지 않는다. */
function reportFontFace(href: string): string {
  return `@font-face{font-family:"Weekly Report";src:url("${href}") format("truetype");font-weight:400;font-style:normal;font-display:swap;}`;
}

const REPORT_INK = "#111827";
const REPORT_TEXT = "#1f2937";
const REPORT_LABEL = "#374151";
const REPORT_QUIET = "#4b5563";
const REPORT_LINE = "#d1d5db";
const REPORT_RULE = "#9ca3af";
const REPORT_WASH = "#f3f4f6";
const REPORT_BAND = "#f8fafc";
const REPORT_PAPER = "#ffffff";
const REPORT_SHADE = "#fafafa";
const REPORT_HOVER = "#f3f4f6";
const REPORT_SELECTED = "#f1f5f9";
const grid = `1px solid ${REPORT_LINE}`;

const PRINT_STYLES = `
@media print {
  [data-app-nav],
  [data-app-topbar],
  [data-chrome-toggle],
  [data-chrome-search],
  [data-print-hide],
  [data-ghost-row],
  [data-suggestion-drawer],
  [data-inline-action],
  .bullet-actions {
    display: none !important;
  }
  body, html {
    background: #ffffff !important;
    color: #111827 !important;
    font-size: 11pt !important;
    filter: grayscale(1);
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  [data-app-shell],
  [data-app-well],
  main {
    display: block !important;
    height: auto !important;
    overflow: visible !important;
    background: #ffffff !important;
    border: none !important;
    padding: 0 !important;
    margin: 0 !important;
  }
  [data-weekly-report] {
    height: auto !important;
    overflow: visible !important;
    background: #ffffff !important;
    padding: 0 !important;
  }
  .report-sheet {
    box-shadow: none !important;
    border: 1px solid #d1d5db !important;
    margin: 0 !important;
    max-width: 100% !important;
    width: 100% !important;
  }
  .avoid-break {
    break-inside: avoid !important;
    page-break-inside: avoid !important;
  }
}
@page {
  size: A4 landscape;
  margin: 10mm 12mm 10mm 12mm;
}
`;

const useStyles = makeStyles({
  page: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    height: "100%",
    minWidth: 0,
    minHeight: 0,
    backgroundColor: tokens.colorNeutralBackground2,
    "@media print": {
      height: "auto",
      overflow: "visible",
      backgroundColor: "#ffffff",
    },
  },
  mockBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "24px",
    height: "24px",
    padding: 0,
    border: "none",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: "transparent",
    color: tokens.colorNeutralForeground3,
    cursor: "pointer",
    flexShrink: 0,
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      color: tokens.colorNeutralForeground1,
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
  },
  column: {
    ...contentShell,
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: "0%",
    minHeight: 0,
  },
  body: {
    ...listBody,
    minWidth: 0,
    paddingLeft: shellGutter,
    paddingRight: shellGutter,
    paddingBottom: tokens.spacingVerticalXXL,
    display: "flex",
    flexDirection: "column",
    "@media print": {
      padding: 0,
      overflow: "visible",
    },
  },
  toolbarActions: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexWrap: "wrap",
  },
  weekSelect: {
    width: "176px",
    flexShrink: 0,
  },
  versionSelect: {
    width: "92px",
    flexShrink: 0,
  },
  paper: {
    width: "100%",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    gap: "20px",
    padding: "28px 32px 36px",
    backgroundColor: REPORT_PAPER,
    border: grid,
    boxShadow: "0 1px 2px rgba(17, 24, 39, 0.06)",
    color: REPORT_TEXT,
    fontFamily: '"Weekly Report", "Apple SD Gothic Neo", sans-serif',
    fontSize: "13px",
    lineHeight: "18px",
    fontWeight: tokens.fontWeightRegular,
    fontVariantNumeric: "tabular-nums",
    "@media print": {
      padding: "16px 20px",
      gap: "16px",
      boxShadow: "none",
    },
  },
  docHeader: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: "16px",
    flexWrap: "wrap",
    paddingBottom: "12px",
    borderBottom: `2px solid ${REPORT_INK}`,
  },
  reportTitle: {
    margin: 0,
    fontFamily: "inherit",
    fontSize: "20px",
    lineHeight: "24px",
    fontWeight: tokens.fontWeightRegular,
    letterSpacing: "-0.02em",
    color: REPORT_INK,
    whiteSpace: "nowrap",
  },
  titleInput: {
    fontFamily: "inherit",
    fontSize: "20px",
    lineHeight: "24px",
    fontWeight: tokens.fontWeightRegular,
    letterSpacing: "-0.02em",
    color: REPORT_INK,
    border: "1px solid transparent",
    borderRadius: "4px",
    padding: "2px 6px",
    marginLeft: "-6px",
    backgroundColor: "transparent",
    minWidth: "260px",
    maxWidth: "100%",
    "&:hover": {
      border: `1px solid ${REPORT_LINE}`,
      backgroundColor: REPORT_BAND,
    },
    "&:focus": {
      border: `1px solid ${REPORT_INK}`,
      backgroundColor: REPORT_PAPER,
    },
    "&:focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
  },
  metaStamp: {
    display: "flex",
    flexDirection: "row",
    alignItems: "baseline",
    gap: "16px",
    flexWrap: "wrap",
    fontSize: "13px",
    lineHeight: "18px",
    fontWeight: tokens.fontWeightRegular,
    fontVariantNumeric: "tabular-nums",
  },
  stampRow: {
    display: "flex",
    gap: "8px",
  },
  stampLabel: {
    color: REPORT_QUIET,
    fontWeight: tokens.fontWeightRegular,
  },
  stampValue: {
    color: REPORT_INK,
    fontWeight: tokens.fontWeightRegular,
  },
  quiet: {
    color: REPORT_QUIET,
    fontWeight: tokens.fontWeightRegular,
    fontVariantNumeric: "tabular-nums",
  },
  infoGrid: {
    width: "100%",
    borderCollapse: "collapse",
    border: grid,
    fontSize: "13px",
    lineHeight: "18px",
    backgroundColor: REPORT_PAPER,
  },
  thLabel: {
    color: REPORT_LABEL,
    fontWeight: tokens.fontWeightRegular,
    fontSize: "13px",
    lineHeight: "18px",
    padding: "8px 12px",
    textAlign: "left",
    backgroundColor: REPORT_WASH,
    border: grid,
    width: "14%",
    whiteSpace: "nowrap",
    verticalAlign: "middle",
  },
  tdValue: {
    color: REPORT_INK,
    fontWeight: tokens.fontWeightRegular,
    fontSize: "13px",
    lineHeight: "18px",
    padding: "8px 12px",
    backgroundColor: REPORT_PAPER,
    border: grid,
    width: "36%",
    verticalAlign: "middle",
  },
  tdBand: {
    backgroundColor: REPORT_SHADE,
  },
  cellSelect: {
    width: "100%",
    minWidth: 0,
  },
  verdictSelect: {
    width: "148px",
    flexShrink: 0,
    alignSelf: "center",
  },
  printOnly: {
    display: "none",
    "@media print": {
      display: "inline",
    },
  },
  inlineTextInput: {
    fontFamily: "inherit",
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    color: tokens.colorNeutralForeground1,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    padding: "3px 8px",
    width: "100%",
    boxSizing: "border-box",
    minHeight: "24px",
    "&:hover": {
      border: `1px solid ${tokens.colorNeutralStroke1Hover}`,
    },
    "&:focus": {
      border: `1px solid ${tokens.colorBrandStroke1}`,
    },
    "&:focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
  },
  autosaveIndicator: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "12px",
    color: tokens.colorNeutralForeground3,
    padding: "2px 8px",
    borderRadius: "12px",
    backgroundColor: tokens.colorNeutralBackground3,
  },
  autosaveDot: {
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    backgroundColor: tokens.colorNeutralForeground3,
  },
  inlineVerdictBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    background: "none",
    border: "none",
    padding: "1px 4px",
    borderRadius: "4px",
    cursor: "pointer",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
    "@media print": {
      padding: 0,
      backgroundColor: "transparent !important",
      cursor: "default !important",
    },
  },
  inlinePmBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    background: "none",
    border: "none",
    padding: "2px 4px",
    borderRadius: "4px",
    cursor: "pointer",
    fontFamily: "inherit",
    fontSize: "inherit",
    fontWeight: "inherit",
    color: "inherit",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
    "@media print": {
      padding: 0,
      backgroundColor: "transparent !important",
      cursor: "default !important",
    },
  },
  inlineCaret: {
    display: "inline-flex",
    color: tokens.colorNeutralForeground3,
    "@media print": {
      display: "none !important",
    },
  },
  bulletRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: "4px",
    position: "relative",
    padding: "1px 0",
    "&:hover .bullet-actions": {
      opacity: 1,
    },
  },
  bulletBullet: {
    flexShrink: 0,
    marginRight: "4px",
    color: REPORT_QUIET,
  },
  bulletContent: {
    flexGrow: 1,
    minWidth: 0,
  },
  bulletActions: {
    opacity: 0,
    display: "inline-flex",
    alignItems: "center",
    gap: "2px",
    transition: "opacity 0.15s ease",
    flexShrink: 0,
    "@media print": {
      display: "none !important",
    },
  },
  bulletActionBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "none",
    border: "none",
    padding: "1px 4px",
    borderRadius: "3px",
    cursor: "pointer",
    color: tokens.colorNeutralForeground3,
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      color: tokens.colorNeutralForeground1,
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
    "@media print": {
      display: "none !important",
    },
  },
  inlineCardItem: {
    position: "relative",
    "&:hover .bullet-actions": {
      opacity: 1,
    },
  },
  requestTitleRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "4px",
  },
  rowMenuBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "none",
    border: "none",
    padding: "2px",
    borderRadius: "3px",
    cursor: "pointer",
    color: tokens.colorNeutralForeground3,
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      color: tokens.colorNeutralForeground1,
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
    "@media print": {
      display: "none !important",
    },
  },
  inlineMemo: {
    width: "100%",
  },
  inlineTag: {
    display: "inline-block",
    fontSize: "10px",
    fontWeight: 600,
    padding: "0 4px",
    borderRadius: "3px",
    marginRight: "4px",
    verticalAlign: "middle",
  },
  tagManual: {
    backgroundColor: "rgba(139, 92, 246, 0.12)",
    color: "#6d28d9",
  },
  tagEdited: {
    backgroundColor: "rgba(245, 158, 11, 0.15)",
    color: "#92400e",
  },
  tagLinked: {
    backgroundColor: "rgba(59, 130, 246, 0.12)",
    color: "#1e40af",
  },
  editFrame: {
    display: "flex",
    flexDirection: "column",
    flex: "1 1 auto",
    height: "100%",
    minHeight: 0,
    overflow: "hidden",
  },
  editShell: {
    display: "flex",
    flex: "1 1 auto",
    minHeight: 0,
    overflow: "hidden",
    backgroundColor: tokens.colorNeutralBackground1,
    "@media (max-width: 760px)": {
      flexDirection: "column",
    },
  },
  editNav: {
    width: "220px",
    flexShrink: 0,
    minHeight: 0,
    overflow: "auto",
    padding: "12px 8px",
    backgroundColor: tokens.colorNeutralBackground3,
    borderRightWidth: "1px",
    borderRightStyle: "solid",
    borderRightColor: tokens.colorNeutralStroke2,
    "@media (max-width: 760px)": {
      width: "100%",
      maxHeight: "180px",
      borderRightWidth: "0px",
      borderBottomWidth: "1px",
      borderBottomStyle: "solid",
      borderBottomColor: tokens.colorNeutralStroke2,
    },
  },
  editGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },
  editGroupLabel: {
    margin: 0,
    padding: "6px 10px 2px",
    fontSize: "12px",
    lineHeight: "16px",
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground3,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  editItem: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    width: "100%",
    height: "36px",
    padding: "0 10px",
    border: "none",
    borderRadius: tokens.borderRadiusLarge,
    backgroundColor: "transparent",
    color: tokens.colorNeutralForeground1,
    cursor: "pointer",
    textAlign: "left",
    fontSize: "13px",
    lineHeight: "18px",
    fontWeight: tokens.fontWeightRegular,
    transitionProperty: "background-color, color",
    transitionDuration: tokens.durationFaster,
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Selected,
    },
    ":active": {
      backgroundColor: tokens.colorNeutralBackground1Pressed,
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
  },
  editItemOn: {
    backgroundColor: tokens.colorNeutralBackground1Selected,
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Selected,
    },
    ":active": {
      backgroundColor: tokens.colorNeutralBackground1Pressed,
    },
  },
  editIcon: {
    display: "inline-flex",
    flexShrink: 0,
    color: tokens.colorNeutralForeground3,
  },
  editIconOn: {
    color: tokens.colorNeutralForeground1,
  },
  editItemLabel: {
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  editContent: {
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    padding: "20px 28px 0",
  },
  editContentHead: {
    flexShrink: 0,
    marginBottom: "16px",
    paddingRight: "16px",
  },
  editScroll: {
    flex: 1,
    minHeight: 0,
    overflow: "auto",
    paddingBottom: "24px",
  },
  editStack: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    minWidth: 0,
    width: "100%",
  },
  editCard: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    padding: "20px",
    borderRadius: tokens.borderRadiusLarge,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  editCardHead: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    minWidth: 0,
  },
  editCardHeadRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "12px",
  },
  editCardTitle: {
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground1,
  },
  editCardSubtitle: {
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    color: tokens.colorNeutralForeground3,
  },
  editFoot: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
    flexShrink: 0,
    padding: "8px 16px 16px",
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightRegular,
  },
  coverForm: {
    display: "flex",
    flexDirection: "column",
    gap: "14px",
  },
  coverGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: "12px",
  },
  coverRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    flexGrow: 1,
    minHeight: "320px",
    textAlign: "center",
    color: tokens.colorNeutralForeground3,
  },
  emptyTitle: {
    margin: 0,
    fontSize: tokens.fontSizeBase400,
    lineHeight: tokens.lineHeightBase400,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground1,
  },
  emptyHint: {
    margin: 0,
    marginBottom: "8px",
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
  },
  memoInput: {
    minHeight: "54px",
    resize: "vertical",
    display: "block",
  },
  kpiHighlightBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    flexWrap: "wrap",
    fontVariantNumeric: "tabular-nums",
  },
  kpiMetricsRow: {
    display: "flex",
    alignItems: "baseline",
    gap: "16px",
    flexWrap: "wrap",
    flexGrow: 1,
    minWidth: 0,
  },
  kpiItem: {
    display: "inline-flex",
    alignItems: "baseline",
    gap: "4px",
  },
  kpiLabel: {
    color: REPORT_QUIET,
    fontSize: "13px",
    lineHeight: "18px",
    fontWeight: tokens.fontWeightRegular,
  },
  kpiNumber: {
    fontSize: "15px",
    lineHeight: "20px",
    fontWeight: tokens.fontWeightRegular,
    letterSpacing: "-0.01em",
    fontVariantNumeric: "tabular-nums",
    color: REPORT_INK,
  },
  section: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    width: "100%",
  },
  sectionTitleBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    borderBottom: `1px solid ${REPORT_LINE}`,
    paddingBottom: "4px",
    flexWrap: "wrap",
  },
  sectionHeading: {
    margin: 0,
    flexShrink: 0,
    fontFamily: "inherit",
    fontSize: "15px",
    lineHeight: "20px",
    fontWeight: tokens.fontWeightRegular,
    letterSpacing: "-0.01em",
    color: REPORT_INK,
    display: "flex",
    alignItems: "center",
    gap: "8px",
    "&::before": {
      content: '""',
      width: "3px",
      height: "14px",
      backgroundColor: REPORT_INK,
      flexShrink: 0,
    },
  },
  sectionTitleActions: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexWrap: "wrap",
  },
  sectionHint: {
    color: REPORT_QUIET,
    fontSize: "11px",
    lineHeight: "14px",
    fontWeight: tokens.fontWeightRegular,
    textAlign: "right",
  },
  summaryStack: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
  },
  summaryTable: {
    width: "100%",
    borderCollapse: "collapse",
    border: grid,
    fontSize: "13px",
    lineHeight: "18px",
    tableLayout: "fixed",
    backgroundColor: REPORT_PAPER,
  },
  summaryColHeader: {
    backgroundColor: REPORT_WASH,
    color: REPORT_INK,
    fontWeight: tokens.fontWeightRegular,
    padding: "8px 10px",
    textAlign: "left",
    border: grid,
    borderBottom: `2px solid ${REPORT_RULE}`,
    fontSize: "13px",
    lineHeight: "18px",
  },
  summaryColHeaderInner: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
  },
  summaryColHeaderSub: {
    marginLeft: "6px",
    fontWeight: tokens.fontWeightRegular,
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
    color: REPORT_QUIET,
  },
  summaryCell: {
    verticalAlign: "top",
    padding: "10px",
    border: grid,
    lineHeight: "18px",
    fontWeight: tokens.fontWeightRegular,
    backgroundColor: REPORT_PAPER,
    color: REPORT_TEXT,
  },
  verdict: {
    display: "inline-flex",
    alignItems: "baseline",
    gap: "6px",
    minWidth: 0,
  },
  summaryEmpty: {
    color: REPORT_QUIET,
    fontSize: "13px",
    lineHeight: "18px",
  },
  bulletList: {
    margin: 0,
    paddingLeft: "16px",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    color: REPORT_TEXT,
    fontSize: "13px",
    lineHeight: "18px",
    fontWeight: tokens.fontWeightRegular,
  },
  bulletItem: {
    lineHeight: 1.45,
  },
  compactAgendaItem: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    paddingBottom: "8px",
    marginBottom: "8px",
    borderBottom: grid,
    "&:last-child": {
      borderBottom: "none",
      paddingBottom: 0,
      marginBottom: 0,
    },
  },
  agendaDateRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    color: REPORT_QUIET,
    fontWeight: tokens.fontWeightRegular,
    fontVariantNumeric: "tabular-nums",
  },
  agendaTitle: {
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightRegular,
    color: REPORT_INK,
  },
  compactIssueItem: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    paddingBottom: "8px",
    marginBottom: "8px",
    borderBottom: grid,
    "&:last-child": {
      borderBottom: "none",
      paddingBottom: 0,
      marginBottom: 0,
    },
  },
  issueTitleRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "8px",
  },
  issueTitle: {
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightRegular,
    color: REPORT_INK,
  },
  issueActionText: {
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    color: REPORT_LABEL,
    fontWeight: tokens.fontWeightRegular,
    display: "flex",
    alignItems: "baseline",
    gap: "4px",
  },
  requestItem: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    paddingBottom: "8px",
    marginBottom: "8px",
    borderBottom: grid,
    "&:last-child": {
      borderBottom: "none",
      paddingBottom: 0,
      marginBottom: 0,
    },
  },
  requestTitle: {
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightRegular,
    color: REPORT_INK,
  },
  requestMeta: {
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    color: REPORT_QUIET,
    fontWeight: tokens.fontWeightRegular,
    display: "flex",
    justifyContent: "space-between",
    gap: "8px",
    fontVariantNumeric: "tabular-nums",
  },
  // 목록 관리 패널 (이번 보고 / 제안 두 무더기)
  listManagerBox: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  listManagerHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
    flexWrap: "wrap",
  },
  pileBlock: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  pileHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
    flexWrap: "wrap",
  },
  pileTitle: {
    fontSize: "12px",
    lineHeight: "16px",
    color: tokens.colorNeutralForeground2,
    fontWeight: tokens.fontWeightRegular,
  },
  pileList: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  pileRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "6px 8px",
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    color: tokens.colorNeutralForeground1,
    fontSize: "13px",
    lineHeight: "18px",
  },
  dragHandle: {
    cursor: "grab",
    color: REPORT_QUIET,
    display: "inline-flex",
    alignItems: "center",
    flexShrink: 0,
  },
  pileRowBody: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexGrow: 1,
    minWidth: 0,
    flexWrap: "wrap",
  },
  manualAddRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "6px 8px",
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px dashed ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
  },
  pastSearchBox: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    padding: "8px 10px",
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
  },
  pastSearchResults: {
    maxHeight: "180px",
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  pastSearchItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
    padding: "4px 6px",
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
    "&:last-child": {
      borderBottom: "none",
    },
  },
  sectionPopover: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    padding: "4px 0",
  },
  sectionCheckList: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  scroll: {
    width: "100%",
    overflowX: "auto",
  },
  wbsTable: {
    width: "100%",
    borderCollapse: "collapse",
    border: grid,
    fontSize: "13px",
    lineHeight: "18px",
    backgroundColor: REPORT_PAPER,
  },
  wbsTh: {
    backgroundColor: REPORT_WASH,
    color: REPORT_INK,
    fontWeight: tokens.fontWeightRegular,
    textAlign: "left",
    border: grid,
    padding: "6px 8px",
    fontSize: "13px",
    lineHeight: "18px",
    whiteSpace: "nowrap",
    verticalAlign: "middle",
  },
  wbsThGroup: {
    textAlign: "center",
  },
  headRule: {
    borderBottom: `2px solid ${REPORT_RULE}`,
  },
  num: {
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
    fontFeatureSettings: '"tnum"',
  },
  center: {
    textAlign: "center",
  },
  phaseRow: {
    "& td": { backgroundColor: REPORT_BAND },
    "&:hover td": { backgroundColor: REPORT_SELECTED },
  },
  phaseCellCode: {
    padding: "6px 8px",
    color: REPORT_INK,
    fontWeight: tokens.fontWeightRegular,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    border: grid,
    whiteSpace: "nowrap",
    fontVariantNumeric: "tabular-nums",
  },
  phaseCellName: {
    padding: "6px 8px",
    color: REPORT_INK,
    fontWeight: tokens.fontWeightRegular,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    border: grid,
  },
  phaseCellDate: {
    padding: "6px 8px",
    textAlign: "right",
    color: REPORT_INK,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightRegular,
    fontVariantNumeric: "tabular-nums",
    border: grid,
    whiteSpace: "nowrap",
  },
  phaseCellProgress: {
    padding: "6px 8px",
    textAlign: "right",
    fontWeight: tokens.fontWeightRegular,
    color: REPORT_INK,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontVariantNumeric: "tabular-nums",
    border: grid,
    whiteSpace: "nowrap",
  },
  phaseCellDiscipline: {
    padding: "6px 8px",
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightRegular,
    color: REPORT_INK,
    border: grid,
    verticalAlign: "middle",
  },
  activityRow: {
    "& td": { backgroundColor: REPORT_PAPER },
    "&:hover td": { backgroundColor: REPORT_HOVER },
  },
  actCellCode: {
    padding: "6px 8px",
    color: REPORT_INK,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightRegular,
    fontVariantNumeric: "tabular-nums",
    border: grid,
    textAlign: "left",
    whiteSpace: "nowrap",
    verticalAlign: "middle",
  },
  actCellName: {
    padding: "6px 8px 6px 20px",
    color: REPORT_INK,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightRegular,
    border: grid,
    verticalAlign: "middle",
  },
  plainName: {
    padding: "6px 8px",
    color: REPORT_INK,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightRegular,
    border: grid,
    verticalAlign: "middle",
  },
  actCellDate: {
    padding: "6px 8px",
    textAlign: "right",
    color: REPORT_INK,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightRegular,
    fontVariantNumeric: "tabular-nums",
    border: grid,
    whiteSpace: "nowrap",
    verticalAlign: "middle",
  },
  actCellProgress: {
    padding: "6px 8px",
    textAlign: "right",
    fontWeight: tokens.fontWeightRegular,
    color: REPORT_INK,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontVariantNumeric: "tabular-nums",
    border: grid,
    whiteSpace: "nowrap",
    verticalAlign: "middle",
  },
  actCellDiscipline: {
    padding: "6px 8px",
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightRegular,
    color: REPORT_INK,
    border: grid,
    verticalAlign: "middle",
  },
  centerCell: {
    padding: "6px 8px",
    textAlign: "center",
    border: grid,
    verticalAlign: "middle",
  },
  deliverableLink: {
    display: "inline-flex",
    alignItems: "flex-start",
    gap: "4px",
    maxWidth: "100%",
    color: REPORT_INK,
    fontSize: "13px",
    lineHeight: "18px",
    fontWeight: tokens.fontWeightRegular,
    "& svg": {
      width: "14px",
      height: "14px",
      marginTop: "2px",
      flexShrink: 0,
      color: REPORT_LABEL,
    },
  },
  deliverableName: {
    textDecoration: "underline",
    textUnderlineOffset: "2px",
    textDecorationColor: REPORT_RULE,
  },
  disciplineBody: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "2px",
    minWidth: 0,
  },
  historyTable: {
    width: "100%",
    borderCollapse: "collapse",
    border: grid,
    fontSize: "13px",
    lineHeight: "18px",
    backgroundColor: REPORT_PAPER,
  },
  historyTh: {
    backgroundColor: REPORT_WASH,
    color: REPORT_INK,
    fontWeight: tokens.fontWeightRegular,
    padding: "6px 8px",
    border: grid,
    borderBottom: `2px solid ${REPORT_RULE}`,
    fontSize: "13px",
    lineHeight: "18px",
    textAlign: "left",
    whiteSpace: "nowrap",
  },
  historyThNum: {
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
  },
  historyRow: {
    cursor: "pointer",
    "& td": { backgroundColor: REPORT_PAPER },
    "&:hover td": { backgroundColor: REPORT_HOVER },
    "&:active td": { backgroundColor: REPORT_WASH },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "-2px",
    },
  },
  historyRowSelected: {
    "& td": { backgroundColor: REPORT_SELECTED },
    "&:hover td": { backgroundColor: REPORT_SELECTED },
  },
  historyTd: {
    padding: "6px 8px",
    border: grid,
    color: REPORT_INK,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightRegular,
    verticalAlign: "middle",
  },
  historyTdNum: {
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
    fontFeatureSettings: '"tnum"',
    whiteSpace: "nowrap",
  },
});

type EditTab = "cover" | WeeklyListKey;

const EDIT_NAV: Array<{ key: EditTab; label: string; icon: FluentIcon }> = [
  { key: "cover", label: "표지", icon: Document16Regular },
  { key: "prevTasks", label: "지난주", icon: ArrowLeft16Regular },
  { key: "currentTasks", label: "금주", icon: CalendarLtr16Regular },
  { key: "nextTasks", label: "차주", icon: ArrowRight16Regular },
  { key: "agendas", label: "보고일정", icon: CalendarClock16Regular },
  { key: "issues", label: "이슈·리스크", icon: Warning16Regular },
  { key: "requests", label: "요청", icon: Mail16Regular },
  { key: "wbs", label: "업무 진행상황", icon: ClipboardTaskList16Regular },
];

function entryStateLabel(entry: WeeklyEntryMeta): string {
  if (entry.state === "draft") return "초안";
  if (entry.state === "closed") return "마감";
  return "작성 전";
}

function WeeklyMockMenu({ styles }: { styles: ReturnType<typeof useStyles> }) {
  const scenario = useWeeklyMockCase();
  return (
    <Menu
      checkedValues={{ case: [scenario] }}
      onCheckedValueChange={(_, data) => {
        const next = data.checkedItems[0];
        if (next === "empty" || next === "current") setWeeklyMockCase(next);
      }}
      positioning="below-start"
    >
      <MenuTrigger disableButtonEnhancement>
        <button type="button" className={styles.mockBtn} aria-label="목업 사례" title="목업 사례">
          <MoreHorizontal16Regular />
        </button>
      </MenuTrigger>
      <MenuPopover>
        <MenuList>
          {WEEKLY_MOCK_CASES.map((item) => (
            <MenuItemRadio key={item.id} name="case" value={item.id}>
              {item.label}
            </MenuItemRadio>
          ))}
        </MenuList>
      </MenuPopover>
    </Menu>
  );
}

export function ProjectWeeklyReportPage() {
  const [params] = useSearchParams();
  if (params.get("scope") === "assignee") return <PlanAssigneeWeekly />;
  return <ProjectWeeklyReportDocument />;
}

function ProjectWeeklyReportDocument() {
  const styles = useStyles();
  const project = useProjectStore((state) => state.project);
  const mockCase = useWeeklyMockCase();
  const shown = useMemo(() => weeklyProjectForMock(project, mockCase), [project, mockCase]);
  const resources = useCatalogStore((state) => state.resources);
  const actorName = useCatalogStore((state) => state.profile.displayName);
  const ensureWeeklyDraft = useProjectStore((state) => state.ensureWeeklyDraft);
  const updateWeeklyDraft = useProjectStore((state) => state.updateWeeklyDraft);
  const closeWeeklyReport = useProjectStore((state) => state.closeWeeklyReport);
  const discardWeeklyDraft = useProjectStore((state) => state.discardWeeklyDraft);
  const openWeeklyRevision = useProjectStore((state) => state.openWeeklyRevision);

  const canFreeze = useCan("plan.schedule.edit");
  const canExport = useCan("plan.export");

  const [selectedDate, setSelectedDate] = useState<ISODate>(project.statusDate);
  const [selectedVersion, setSelectedVersion] = useState<number | undefined>(undefined);
  const [confirmCloseState, setConfirmCloseState] = useState<{ open: boolean; exportPdfAfter: boolean }>({
    open: false,
    exportPdfAfter: false,
  });
  const [showHiddenInFrozen, setShowHiddenInFrozen] = useState(false);

  // 편집 대화상자, 작성 대화상자, 초안 버리기 확인
  const [editOpen, setEditOpen] = useState(false);
  const [editTab, setEditTab] = useState<EditTab>("cover");
  const [createOpen, setCreateOpen] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [activeNewIssueId, setActiveNewIssueId] = useState<string | null>(null);

  const reportRef = useRef<HTMLDivElement>(null);

  const handleAddNewIssue = () => {
    const newIssueId = newId("wline");
    setActiveNewIssueId(newIssueId);
    updateWeeklyDraft(selectedDate, (d) =>
      addManualDraftLine(d, "issues", {
        id: newIssueId,
        text: "새 이슈",
        secondary: "대응 방안 검토 중",
        severity: "Medium",
        status: "open",
      }),
    );
  };

  useEffect(() => {
    setSelectedDate(project.statusDate);
    setSelectedVersion(undefined);
    setShowHiddenInFrozen(false);
  }, [project.id, project.statusDate]);

  useEffect(() => {
    const draft = shown.weeklyDrafts?.[selectedDate];
    if (!draft || draft.weekMemo !== undefined) return;
    const memo = seedWeekMemo(project.id, selectedDate, project.statusDate);
    if (!memo) return;
    updateWeeklyDraft(selectedDate, (d) =>
      d.weekMemo !== undefined ? d : { ...d, weekMemo: memo, updatedAt: new Date().toISOString() },
    );
  }, [shown, project.id, project.statusDate, selectedDate, updateWeeklyDraft]);

  const entries = useMemo(() => listWeeklyEntries(shown), [shown]);
  const unclosedPastEntry = useMemo(() => entries.find((e) => e.isUnclosedPast), [entries]);

  const activeDraft: WeeklyReportDraft | null = useMemo(() => {
    // 페이지를 열 때 초안을 자동으로 심지 않는다. 초안은 「작성」으로만 생긴다.
    return shown.weeklyDrafts?.[selectedDate] ?? null;
  }, [shown, selectedDate]);

  const closedSnapshotsForDate = useMemo(
    () => findWeeklySnapshotsForDate(shown, selectedDate),
    [shown, selectedDate],
  );
  const activeClosedSnapshot = useMemo(
    () => (activeDraft ? undefined : findWeeklySnapshot(shown, selectedDate, selectedDate, selectedVersion)),
    [activeDraft, shown, selectedDate, selectedVersion],
  );

  const isDraft = !!activeDraft;
  const isNone = !activeDraft && closedSnapshotsForDate.length === 0;
  const canEditDraft = isDraft && canFreeze;

  const snapshot: WeeklyV2WeekSnapshot = useMemo(() => {
    if (activeDraft) {
      return buildReportFromDraft(activeDraft, project);
    }
    const frozenReport = readFrozenReport(activeClosedSnapshot);
    if (frozenReport) return frozenReport;
    const fallbackDraft = seedWeeklyDraft(shown, resources, selectedDate, actorName);
    return buildReportFromDraft(fallbackDraft, shown);
  }, [activeDraft, activeClosedSnapshot, shown, resources, selectedDate, actorName]);

  const history = useMemo(
    () => buildWeeklyHistoryFromProject(shown, activeDraft),
    [shown, activeDraft],
  );

  const pmOptions = useMemo(() => pmPersonOptions(project, resources), [project, resources]);

  const savedSections: WeeklySectionVisibility = snapshot.sections ?? DEFAULT_WEEKLY_SECTIONS;
  const effectiveSections: WeeklySectionVisibility = !isDraft && showHiddenInFrozen
    ? { cost: true, wbs: true, history: true }
    : savedSections;
  const hasHiddenSectionsInClosed = !isDraft && (!savedSections.cost || !savedSections.wbs || !savedSections.history);

  const handleSelectWeek = (nextDate: string) => {
    if (!isISODate(nextDate)) return;
    setSelectedDate(nextDate);
    setSelectedVersion(undefined);
    setShowHiddenInFrozen(false);
    setEditOpen(false);
  };

  const handlePrint = () => {
    window.print();
  };

  const downloadPdfForSnapshot = async (reportSource: WeeklyV2WeekSnapshot) => {
    const response = await fetch(weeklyFontRequest());
    if (!response.ok) return;
    const bytes = await buildWeeklyPdf(reportSource, new Uint8Array(await response.arrayBuffer()));
    await saveBytes(`${reportSource.reportNo}.pdf`, bytes, "application/pdf");
  };

  const handleConfirmClose = async () => {
    const shouldExport = confirmCloseState.exportPdfAfter;
    setConfirmCloseState({ open: false, exportPdfAfter: false });
    const closedSnap = closeWeeklyReport(selectedDate);
    setSelectedVersion(undefined);
    setEditOpen(false);
    if (shouldExport && closedSnap) {
      const frozenReport = readFrozenReport(closedSnap);
      if (frozenReport) {
        await downloadPdfForSnapshot(frozenReport);
      }
    }
  };

  const handlePdfClick = async () => {
    if (isDraft) {
      if (!canFreeze) return;
      // 초안에서 PDF를 누르면 마감을 먼저 확인한다.
      setConfirmCloseState({ open: true, exportPdfAfter: true });
      return;
    }
    await downloadPdfForSnapshot(snapshot);
  };

  const weekOptions = entries.map((entry) => ({
    value: entry.statusDate,
    label: `${entry.label} · ${entryStateLabel(entry)}`,
  }));

  /** 새 보고서 기준일 기본값: 아직 쓰지 않은 현재 기준일, 없으면 마지막 주 다음 주. */
  const createDefaultDate = useMemo<ISODate>(() => {
    const used = new Map(entries.map((entry) => [entry.statusDate, entry.state]));
    const free = (date: ISODate) => (used.get(date) ?? "none") === "none";
    if (free(project.statusDate)) return project.statusDate;
    const last = entries[entries.length - 1]?.statusDate ?? project.statusDate;
    let next = addDays(last, 7);
    while (!free(next)) next = addDays(next, 7);
    return next;
  }, [entries, project.statusDate]);

  const handleCreate = (date: ISODate, carryOver: boolean) => {
    const existing = useProjectStore.getState().project.weeklyDrafts?.[date];
    if (!existing) {
      ensureWeeklyDraft(date);
      if (carryOver) {
        const state = useProjectStore.getState();
        updateWeeklyDraft(date, (d) => applyCarryOverToDraft(d, state.project, resources));
      }
    }
    setCreateOpen(false);
    handleSelectWeek(date);
  };

  const handleDiscard = () => {
    setConfirmDiscard(false);
    setEditOpen(false);
    setSelectedVersion(undefined);
    discardWeeklyDraft(selectedDate);
  };

  const judged = snapshot.hasBaseline !== false;
  const prevClosedSnap = useMemo(() => findPreviousClosedSnapshot(shown, selectedDate), [shown, selectedDate]);
  const prevClosedReport = useMemo(() => readFrozenReport(prevClosedSnap), [prevClosedSnap]);

  return (
    <div className={styles.page} data-weekly-report="">
      <style>{`${reportFontFace(weeklyFontRequest())}\n${PRINT_STYLES}`}</style>
      <div className={styles.column}>
        <div data-print-hide="">
          <ListToolbar
            title="주간 보고"
            titleAside={<WeeklyMockMenu styles={styles} />}
            filters={<WeeklyScopeSwitch />}
            headingAction={
              <div className={styles.toolbarActions}>
                <div className={styles.weekSelect}>
                  <FuiDropdown
                    aria-label="보고 주차"
                    value={selectedDate}
                    options={weekOptions}
                    onValueChange={handleSelectWeek}
                  />
                </div>

                {!isDraft && closedSnapshotsForDate.length > 1 ? (
                  <div className={styles.versionSelect}>
                    <FuiDropdown
                      aria-label="문서 버전"
                      value={String(selectedVersion ?? (activeClosedSnapshot?.version ?? closedSnapshotsForDate.length))}
                      options={closedSnapshotsForDate.map((s) => ({
                        value: String(s.version ?? 1),
                        label: `v${s.version ?? 1}`,
                      }))}
                      onValueChange={(v) => setSelectedVersion(Number(v))}
                    />
                  </div>
                ) : null}

                {unclosedPastEntry ? (
                  <Badge
                    appearance="tint"
                    color="warning"
                    style={{ cursor: unclosedPastEntry.statusDate !== selectedDate ? "pointer" : "default" }}
                    onClick={() => {
                      if (unclosedPastEntry.statusDate !== selectedDate) {
                        handleSelectWeek(unclosedPastEntry.statusDate);
                      }
                    }}
                    title={`${unclosedPastEntry.label} 초안이 아직 마감되지 않았습니다`}
                  >
                    {unclosedPastEntry.statusDate === selectedDate ? "마감 전" : `${unclosedPastEntry.label} 마감 전`}
                  </Badge>
                ) : null}

                {isDraft ? (
                  <span className={styles.autosaveIndicator} data-print-hide="">
                    <span className={styles.autosaveDot} />
                    초안 편집 중 · 자동 저장됨{activeDraft?.updatedAt ? ` (${formatTimeOnly(activeDraft.updatedAt)})` : ""}
                  </span>
                ) : null}

                {/* 본동사는 보고 있는 문서에 따라 하나: 없으면 작성, 초안이면 섹션/WBS관리, 마감본이면 PDF */}
                {isNone && canFreeze ? (
                  <FuiButton appearance="primary" size="small" icon={<Add16Regular />} onClick={() => setCreateOpen(true)}>
                    작성
                  </FuiButton>
                ) : null}

                {canEditDraft ? (
                  <FuiButton appearance="secondary" size="small" icon={<ClipboardTaskList16Regular />} onClick={() => setEditOpen(true)}>
                    섹션 및 WBS 관리
                  </FuiButton>
                ) : null}

                {canEditDraft ? (
                  <FuiButton
                    appearance="secondary"
                    size="small"
                    onClick={() => setConfirmCloseState({ open: true, exportPdfAfter: false })}
                  >
                    마감
                  </FuiButton>
                ) : null}

                {!isNone && canExport ? (
                  <FuiButton
                    appearance={isDraft ? "secondary" : "primary"}
                    size="small"
                    disabled={isDraft && !canFreeze}
                    onClick={() => void handlePdfClick()}
                  >
                    PDF
                  </FuiButton>
                ) : null}

                {!isNone ? (
                  <FuiButton appearance="secondary" size="small" icon={<Print16Regular />} onClick={handlePrint}>
                    인쇄
                  </FuiButton>
                ) : null}

                {canFreeze || hasHiddenSectionsInClosed ? (
                  <Menu positioning="below-end">
                    <MenuTrigger disableButtonEnhancement>
                      <FuiButton
                        appearance="subtle"
                        size="small"
                        icon={<MoreHorizontal16Regular />}
                        aria-label="문서 메뉴"
                        title="문서 메뉴"
                      />
                    </MenuTrigger>
                    <MenuPopover>
                      <MenuList>
                        {canFreeze ? (
                          <MenuItem icon={<Add16Regular />} onClick={() => setCreateOpen(true)}>
                            새 보고서 작성
                          </MenuItem>
                        ) : null}
                        {!isDraft && !isNone && canFreeze ? (
                          <MenuItem icon={<Edit16Regular />} onClick={() => openWeeklyRevision(selectedDate)}>
                            정정
                          </MenuItem>
                        ) : null}
                        {hasHiddenSectionsInClosed ? (
                          <MenuItem icon={<Eye16Regular />} onClick={() => setShowHiddenInFrozen((prev) => !prev)}>
                            {showHiddenInFrozen ? "숨긴 값 가리기" : "숨긴 값 보기"}
                          </MenuItem>
                        ) : null}
                        {canEditDraft ? (
                          <MenuItem icon={<Delete16Regular />} onClick={() => setConfirmDiscard(true)}>
                            초안 버리기
                          </MenuItem>
                        ) : null}
                      </MenuList>
                    </MenuPopover>
                  </Menu>
                ) : null}
              </div>
            }
          />
        </div>

        <div className={styles.body}>
          {isNone ? (
            <div className={styles.emptyState}>
              <h2 className={styles.emptyTitle}>{shortWeekLabel(selectedDate)} 보고서가 없습니다</h2>
              <p className={styles.emptyHint}>
                {canFreeze ? "작성하면 이 기준일의 초안이 만들어집니다." : "작성 권한이 있는 구성원이 만들 수 있습니다."}
              </p>
              {canFreeze ? (
                <FuiButton appearance="primary" size="small" icon={<Add16Regular />} onClick={() => setCreateOpen(true)}>
                  작성
                </FuiButton>
              ) : null}
            </div>
          ) : (
          <article ref={reportRef} className={mergeClasses(styles.paper, "report-sheet")}>
            {/* 1. 표지와 기본 정보 */}
            <header className={styles.docHeader}>
              {canEditDraft ? (
                <InlineText
                  as="h1"
                  value={activeDraft?.title ?? snapshot.title ?? suggestPeriodName(selectedDate)}
                  onCommit={(next) =>
                    updateWeeklyDraft(selectedDate, (d) => ({
                      ...d,
                      title: next.trim() || suggestPeriodName(selectedDate),
                      updatedAt: new Date().toISOString(),
                    }))
                  }
                  className={styles.reportTitle}
                  placeholder="제목"
                  ariaLabel="보고서 제목"
                />
              ) : (
                <h1 className={styles.reportTitle}>{snapshot.title || snapshot.weekLabel || suggestPeriodName(selectedDate)}</h1>
              )}
              <div className={styles.metaStamp}>
                <div className={styles.stampRow}>
                  <span className={styles.stampLabel}>기준일</span>
                  <span className={styles.stampValue}>{snapshot.asOfDate}</span>
                </div>
                <div className={styles.stampRow}>
                  <span className={styles.stampLabel}>문서번호</span>
                  <span className={styles.stampValue}>
                    {snapshot.reportNo}
                    {isDraft ? " · 초안" : " · 마감본"}
                  </span>
                </div>
              </div>
            </header>

            <table className={styles.infoGrid} aria-label="플랜 기본 정보">
              <tbody>
                <tr>
                  <th className={styles.thLabel} scope="row">플랜</th>
                  <td className={styles.tdValue}>{snapshot.projectName}</td>
                  <th className={styles.thLabel} scope="row">기간</th>
                  <td className={styles.tdValue}>{snapshot.projectPeriod}</td>
                </tr>
                <tr>
                  <th className={styles.thLabel} scope="row">발주사 총괄 PM</th>
                  <td className={styles.tdValue}>
                    {canEditDraft ? (
                      <InlineMemberPopover
                        value={weeklyClientPm(snapshot.clientPm)}
                        options={pmOptions.clientOptions}
                        onChange={(nextPm) =>
                          updateWeeklyDraft(selectedDate, (d) => ({
                            ...d,
                            clientPm: nextPm,
                            updatedAt: new Date().toISOString(),
                          }))
                        }
                        placeholder="발주사 PM 선택"
                        ariaLabel="발주사 총괄 PM"
                      />
                    ) : (
                      weeklyClientPm(snapshot.clientPm)
                    )}
                  </td>
                  <th className={styles.thLabel} scope="row">수행사 PM</th>
                  <td className={styles.tdValue}>
                    {canEditDraft ? (
                      <InlineMemberPopover
                        value={snapshot.vendorPm}
                        options={pmOptions.vendorOptions}
                        onChange={(nextPm) =>
                          updateWeeklyDraft(selectedDate, (d) => ({
                            ...d,
                            vendorPm: nextPm,
                            updatedAt: new Date().toISOString(),
                          }))
                        }
                        placeholder="수행사 PM 선택"
                        ariaLabel="수행사 PM"
                      />
                    ) : (
                      snapshot.vendorPm
                    )}
                  </td>
                </tr>
                {/* 2. 종합 공정 현황 */}
                <tr>
                  <th className={styles.thLabel} scope="row">종합 공정 현황</th>
                  <td className={mergeClasses(styles.tdValue, styles.tdBand)} colSpan={3}>
                    <div className={styles.kpiHighlightBox}>
                      <div className={styles.kpiMetricsRow}>
                        <div className={styles.kpiItem}>
                          <span className={styles.kpiLabel}>계획 공정</span>
                          <span className={styles.kpiNumber}>{judged ? `${snapshot.planProgress.toFixed(1)}%` : "—"}</span>
                        </div>
                        <div className={styles.kpiItem}>
                          <span className={styles.kpiLabel}>실적 공정</span>
                          <span className={styles.kpiNumber}>{snapshot.actualProgress.toFixed(1)}%</span>
                        </div>
                        <div className={styles.kpiItem}>
                          <span className={styles.kpiLabel}>대비 차이</span>
                          {judged ? (
                            <ProgressDelta gap={snapshot.actualProgress - snapshot.planProgress} />
                          ) : (
                            <span className={styles.kpiNumber}>—</span>
                          )}
                        </div>
                        <div className={styles.kpiItem}>
                          <span className={styles.kpiLabel}>평가</span>
                          {canEditDraft ? (
                            <Menu>
                              <MenuTrigger disableButtonEnhancement>
                                <button
                                  type="button"
                                  className={styles.inlineVerdictBtn}
                                  title="평가 변경"
                                >
                                  <StatusWord tone={verdictTone(snapshot.statusVerdict)}>{verdictLabel(snapshot.statusVerdict)}</StatusWord>
                                  {snapshot.verdictEdited ? <MetaBadge>수정</MetaBadge> : null}
                                  <span className={styles.inlineCaret} data-print-hide="">
                                    <ChevronDown12Regular />
                                  </span>
                                </button>
                              </MenuTrigger>
                              <MenuPopover>
                                <MenuList>
                                  <MenuItem onClick={() => updateWeeklyDraft(selectedDate, (d) => ({ ...d, statusVerdict: "normal", verdictEdited: true, updatedAt: new Date().toISOString() }))}>
                                    <StatusWord tone="good">정상</StatusWord>
                                  </MenuItem>
                                  <MenuItem onClick={() => updateWeeklyDraft(selectedDate, (d) => ({ ...d, statusVerdict: "watch", verdictEdited: true, updatedAt: new Date().toISOString() }))}>
                                    <StatusWord tone="watch">주의</StatusWord>
                                  </MenuItem>
                                  <MenuItem onClick={() => updateWeeklyDraft(selectedDate, (d) => ({ ...d, statusVerdict: "critical", verdictEdited: true, updatedAt: new Date().toISOString() }))}>
                                    <StatusWord tone="risk">위험</StatusWord>
                                  </MenuItem>
                                  {activeDraft?.computedVerdict ? (
                                    <MenuItem onClick={() => updateWeeklyDraft(selectedDate, (d) => ({ ...d, statusVerdict: d.computedVerdict, verdictEdited: false, updatedAt: new Date().toISOString() }))}>
                                      계산값으로 되돌리기
                                    </MenuItem>
                                  ) : null}
                                </MenuList>
                              </MenuPopover>
                            </Menu>
                          ) : (
                            <>
                              <StatusWord tone={verdictTone(snapshot.statusVerdict)}>{verdictLabel(snapshot.statusVerdict)}</StatusWord>
                              {snapshot.verdictEdited ? <MetaBadge>수정</MetaBadge> : null}
                            </>
                          )}
                        </div>
                        <span className={styles.quiet}>{snapshot.statusSummary}</span>
                      </div>
                    </div>
                  </td>
                </tr>
                <tr>
                  <th className={styles.thLabel} scope="row">평가 메모</th>
                  <td className={styles.tdValue} colSpan={3}>
                    {canEditDraft ? (
                      <InlineText
                        multiline
                        value={activeDraft?.weekMemo ?? ""}
                        onCommit={(next) =>
                          updateWeeklyDraft(selectedDate, (d) => ({
                            ...d,
                            weekMemo: next,
                            updatedAt: new Date().toISOString(),
                          }))
                        }
                        placeholder="—"
                        className={styles.inlineMemo}
                        ariaLabel="평가 메모"
                      />
                    ) : (
                      snapshot.weekMemo?.trim() || "—"
                    )}
                  </td>
                </tr>
                {/* 비용 성과: 켜져 있을 때만 (또는 마감본에서 숨긴 값 열람 중일 때) */}
                {effectiveSections.cost ? (
                  <tr>
                    <th className={styles.thLabel} scope="row">
                      비용 성과
                      {!isDraft && !savedSections.cost ? (
                        <div className={styles.quiet} style={{ fontSize: "11px" }}>
                          숨긴 값
                        </div>
                      ) : null}
                    </th>
                    <td className={mergeClasses(styles.tdValue, styles.tdBand)} colSpan={3}>
                      <div className={styles.kpiHighlightBox}>
                        <div className={styles.kpiMetricsRow}>
                          {COST_KPI.map((item) => (
                            <div key={item.key} className={styles.kpiItem}>
                              <span className={styles.kpiLabel}>{item.label}</span>
                              <span className={styles.kpiNumber}>
                                {costKpiText(snapshot.costEvm ?? UNAVAILABLE_COST, item.key)}
                              </span>
                            </div>
                          ))}
                        </div>
                        {snapshot.costNote ? <span className={styles.quiet}>{snapshot.costNote}</span> : null}
                      </div>
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>

            {/* 3. 주요 업무 요약 */}
            <section className={styles.section} aria-label="주요 업무 요약">
              <div className={styles.sectionTitleBar}>
                <h2 className={styles.sectionHeading}>주요 업무 요약</h2>
              </div>

              <div className={styles.summaryStack}>
                <table className={mergeClasses(styles.summaryTable, "avoid-break")}>
                  <SummaryCols />
                  <thead>
                    <tr>
                      <th className={styles.summaryColHeader} scope="col">
                        <div className={styles.summaryColHeaderInner}>
                          <span>
                            지난주
                            {snapshot.prevTasksRange || prevClosedReport ? (
                              <span className={styles.summaryColHeaderSub}>
                                ({snapshot.prevTasksRange ?? prevClosedReport?.currentTasksRange})
                              </span>
                            ) : null}
                          </span>
                        </div>
                      </th>
                      <th className={styles.summaryColHeader} scope="col">
                        <div className={styles.summaryColHeaderInner}>
                          <span>
                            금주
                            <span className={styles.summaryColHeaderSub}>({snapshot.currentTasksRange})</span>
                          </span>
                        </div>
                      </th>
                      <th className={styles.summaryColHeader} scope="col">
                        <div className={styles.summaryColHeaderInner}>
                          <span>
                            차주
                            <span className={styles.summaryColHeaderSub}>({snapshot.nextTasksRange})</span>
                          </span>
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className={styles.summaryCell}>
                        <TaskSummaryCell
                          styles={styles}
                          lines={snapshot.prevTaskLines}
                          fallbackStrings={snapshot.prevTasks ?? []}
                          emptyText="—"
                          emptyQuiet
                          isEditable={canEditDraft}
                          draftLines={activeDraft?.prevTasks}
                          onUpdateLine={(id, text) => updateWeeklyDraft(selectedDate, (d) => updateDraftTaskLine(d, "prevTasks", id, { text }))}
                          onExcludeLine={(id) => updateWeeklyDraft(selectedDate, (d) => excludeDraftLine(d, "prevTasks", id))}
                          onAddLine={(afterId, newId) => updateWeeklyDraft(selectedDate, (d) => addManualDraftLine(d, "prevTasks", { id: newId, insertAfterId: afterId, text: "", allowEmpty: true }))}
                          onRestoreLine={(id) => updateWeeklyDraft(selectedDate, (d) => includeDraftLine(d, "prevTasks", id))}
                          onDeleteLine={(id) => updateWeeklyDraft(selectedDate, (d) => deleteDraftLine(d, "prevTasks", id))}
                          onMoveLine={(id, dir) => updateWeeklyDraft(selectedDate, (d) => moveDraftLine(d, "prevTasks", id, dir))}
                        />
                      </td>
                      <td className={styles.summaryCell}>
                        <TaskSummaryCell
                          styles={styles}
                          lines={snapshot.currentTaskLines}
                          fallbackStrings={snapshot.currentTasks}
                          emptyText="이번 주 진행 작업이 없습니다."
                          isEditable={canEditDraft}
                          draftLines={activeDraft?.currentTasks}
                          onUpdateLine={(id, text) => updateWeeklyDraft(selectedDate, (d) => updateDraftTaskLine(d, "currentTasks", id, { text }))}
                          onExcludeLine={(id) => updateWeeklyDraft(selectedDate, (d) => excludeDraftLine(d, "currentTasks", id))}
                          onAddLine={(afterId, newId) => updateWeeklyDraft(selectedDate, (d) => addManualDraftLine(d, "currentTasks", { id: newId, insertAfterId: afterId, text: "", allowEmpty: true }))}
                          onRestoreLine={(id) => updateWeeklyDraft(selectedDate, (d) => includeDraftLine(d, "currentTasks", id))}
                          onDeleteLine={(id) => updateWeeklyDraft(selectedDate, (d) => deleteDraftLine(d, "currentTasks", id))}
                          onMoveLine={(id, dir) => updateWeeklyDraft(selectedDate, (d) => moveDraftLine(d, "currentTasks", id, dir))}
                        />
                      </td>
                      <td className={styles.summaryCell}>
                        <TaskSummaryCell
                          styles={styles}
                          lines={snapshot.nextTaskLines}
                          fallbackStrings={snapshot.nextTasks}
                          emptyText="—"
                          emptyQuiet
                          isEditable={canEditDraft}
                          draftLines={activeDraft?.nextTasks}
                          onUpdateLine={(id, text) => updateWeeklyDraft(selectedDate, (d) => updateDraftTaskLine(d, "nextTasks", id, { text }))}
                          onExcludeLine={(id) => updateWeeklyDraft(selectedDate, (d) => excludeDraftLine(d, "nextTasks", id))}
                          onAddLine={(afterId, newId) => updateWeeklyDraft(selectedDate, (d) => addManualDraftLine(d, "nextTasks", { id: newId, insertAfterId: afterId, text: "", allowEmpty: true }))}
                          onRestoreLine={(id) => updateWeeklyDraft(selectedDate, (d) => includeDraftLine(d, "nextTasks", id))}
                          onDeleteLine={(id) => updateWeeklyDraft(selectedDate, (d) => deleteDraftLine(d, "nextTasks", id))}
                          onMoveLine={(id, dir) => updateWeeklyDraft(selectedDate, (d) => moveDraftLine(d, "nextTasks", id, dir))}
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>

                <table className={mergeClasses(styles.summaryTable, "avoid-break")}>
                  <SummaryCols />
                  <thead>
                    <tr>
                      <th className={styles.summaryColHeader} scope="col">
                        <div className={styles.summaryColHeaderInner}>
                          <span>주요 보고일정</span>
                        </div>
                      </th>
                      <th className={styles.summaryColHeader} scope="col">
                        <div className={styles.summaryColHeaderInner}>
                          <span>이슈 / 리스크사항</span>
                        </div>
                      </th>
                      <th className={styles.summaryColHeader} scope="col">
                        <div className={styles.summaryColHeaderInner}>
                          <span>요청사항</span>
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className={styles.summaryCell}>
                        {canEditDraft && activeDraft ? (
                          (() => {
                            const included = activeDraft.agendas.filter((a) => a.included !== false);
                            const excluded = activeDraft.agendas.filter((a) => a.included === false);
                            return (
                              <div>
                                {included.length === 0 ? (
                                  <span className={styles.quiet}>보고일정이 없습니다.</span>
                                ) : (
                                  included.map((item) => (
                                    <div key={item.id} className={mergeClasses(styles.compactAgendaItem, styles.inlineCardItem)}>
                                      <div className={styles.agendaDateRow}>
                                        <InlineDatePopover
                                          value={item.date}
                                          statusDate={snapshot.asOfDate}
                                          showRelativeBadge
                                          presets={[
                                            { label: "오늘", iso: selectedDate },
                                            { label: "내일", iso: addDays(selectedDate, 1) },
                                            { label: "다음 주", iso: addDays(selectedDate, 7) },
                                          ]}
                                          onChange={(nextDate) =>
                                            updateWeeklyDraft(selectedDate, (d) => updateDraftAgendaLine(d, item.id, { date: nextDate }))
                                          }
                                          ariaLabel="일정 일자"
                                        />
                                        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                                          <InlinePopoverBadge<WeeklyAgendaLine["status"]>
                                            value={item.status}
                                            options={[
                                              { value: "upcoming", label: "예정", tone: "neutral" },
                                              { value: "done", label: "완료", tone: "quiet" },
                                              { value: "shifted", label: "변경", tone: "watch" },
                                            ]}
                                            onChange={(nextStatus) => {
                                              const type = nextStatus === "done" ? "완료" : nextStatus === "shifted" ? "변경" : "예정";
                                              updateWeeklyDraft(selectedDate, (d) =>
                                                updateDraftAgendaLine(d, item.id, { status: nextStatus, type }),
                                              );
                                            }}
                                            ariaLabel="일정 상태"
                                          />
                                          <button
                                            type="button"
                                            className={styles.bulletActionBtn}
                                            onClick={() => updateWeeklyDraft(selectedDate, (d) => excludeDraftLine(d, "agendas", item.id))}
                                            aria-label="보고서에서 제외"
                                            data-print-hide=""
                                          >
                                            <Dismiss12Regular />
                                          </button>
                                        </div>
                                      </div>
                                      <div className={styles.agendaTitle}>
                                        {item.origin === "manual" ? <MetaBadge lead title="이 보고에만 있습니다">직접</MetaBadge> : null}
                                        <InlineText
                                          value={item.title}
                                          onCommit={(next) =>
                                            updateWeeklyDraft(selectedDate, (d) => updateDraftAgendaLine(d, item.id, { title: next }))
                                          }
                                          ariaLabel="일정 제목"
                                        />
                                        {item.missingSource ? <MetaBadge danger>원본 없음</MetaBadge> : null}
                                      </div>
                                      <div className={styles.quiet}>
                                        <InlineMemberPopover
                                          value={item.owner}
                                          options={pmOptions.vendorOptions}
                                          onChange={(next) =>
                                            updateWeeklyDraft(selectedDate, (d) => updateDraftAgendaLine(d, item.id, { owner: next }))
                                          }
                                          placeholder="—"
                                          ariaLabel="담당자"
                                        />
                                      </div>
                                    </div>
                                  ))
                                )}
                                <InlineGhostRow
                                  label="보고일정 추가"
                                  onClick={() => {
                                    updateWeeklyDraft(selectedDate, (d) =>
                                      addManualDraftLine(d, "agendas", {
                                        text: "새 보고일정",
                                        status: "upcoming",
                                      }),
                                    );
                                  }}
                                />
                                <SuggestionDrawer
                                  items={excluded.map((a) => ({ id: a.id, label: a.title, secondary: a.date }))}
                                  onRestore={(id) => updateWeeklyDraft(selectedDate, (d) => includeDraftLine(d, "agendas", id))}
                                  label="제외된 보고일정"
                                />
                              </div>
                            );
                          })()
                        ) : snapshot.agendas.length === 0 ? (
                          <span className={styles.quiet}>—</span>
                        ) : (
                          snapshot.agendas.map((item) => (
                            <div key={item.id} className={styles.compactAgendaItem}>
                              <div className={styles.agendaDateRow}>
                                <span>{item.date}</span>
                                <StatusWord tone={agendaTone(item)}>{agendaMark(item)}</StatusWord>
                              </div>
                              <div className={styles.agendaTitle}>
                                {item.title}
                                {item.missingSource ? <MetaBadge danger>원본 없음</MetaBadge> : null}
                              </div>
                              <div className={styles.quiet}>{item.owner}</div>
                            </div>
                          ))
                        )}
                      </td>
                      <td className={styles.summaryCell}>
                        {snapshot.issues.length === 0 ? (
                          <span className={styles.quiet}>—</span>
                        ) : (
                          snapshot.issues.map((iss) => (
                            <div key={iss.id} className={styles.compactIssueItem}>
                              <div className={styles.issueTitleRow}>
                                <span className={styles.quiet}>{iss.date}</span>
                                <SeverityBadge severity={reportSeverity(iss.severity)} />
                              </div>
                              <div className={styles.issueTitle}>
                                {iss.title}
                                {iss.missingSource ? <MetaBadge danger>원본 없음</MetaBadge> : null}
                              </div>
                              <div className={styles.issueActionText}>
                                <span className={styles.quiet} style={{ flexShrink: 0 }}>
                                  대응
                                </span>
                                <span>{iss.action}</span>
                              </div>
                            </div>
                          ))
                        )}
                        {canEditDraft ? (
                          <span className={styles.quiet} style={{ display: "block", marginTop: "8px" }}>
                            이슈는 아래 대장에서 고칩니다.
                          </span>
                        ) : null}
                      </td>
                      <td className={styles.summaryCell}>
                        {canEditDraft && activeDraft ? (
                          (() => {
                            const included = activeDraft.requests.filter((r) => r.included !== false);
                            const excluded = activeDraft.requests.filter((r) => r.included === false);
                            return (
                              <div>
                                {included.length === 0 ? (
                                  <span className={styles.quiet}>요청사항이 없습니다.</span>
                                ) : (
                                  included.map((req) => (
                                    <div key={req.id} className={mergeClasses(styles.requestItem, styles.inlineCardItem)}>
                                      <div className={styles.requestTitleRow}>
                                        <div className={styles.requestTitle}>
                                          {req.origin === "manual" ? <MetaBadge lead title="이 보고에만 있습니다">직접</MetaBadge> : null}
                                          <InlineText
                                            value={req.title}
                                            onCommit={(next) =>
                                              updateWeeklyDraft(selectedDate, (d) => updateDraftRequestLine(d, req.id, { title: next }))
                                            }
                                            ariaLabel="요청 제목"
                                          />
                                          {req.missingSource ? <MetaBadge danger>원본 없음</MetaBadge> : null}
                                        </div>
                                        <button
                                          type="button"
                                          className={styles.bulletActionBtn}
                                          onClick={() => updateWeeklyDraft(selectedDate, (d) => excludeDraftLine(d, "requests", req.id))}
                                          aria-label="보고서에서 제외"
                                          data-print-hide=""
                                        >
                                          <Dismiss12Regular />
                                        </button>
                                      </div>
                                      <div className={styles.requestMeta}>
                                        <span>
                                          기한:{" "}
                                          <InlineDatePopover
                                            value={req.neededBy}
                                            statusDate={snapshot.asOfDate}
                                            presets={[
                                              { label: "이번 주말", iso: addDays(selectedDate, 2) },
                                              { label: "다음 주", iso: addDays(selectedDate, 7) },
                                              { label: "2주 뒤", iso: addDays(selectedDate, 14) },
                                            ]}
                                            onChange={(next) =>
                                              updateWeeklyDraft(selectedDate, (d) => updateDraftRequestLine(d, req.id, { neededBy: next }))
                                            }
                                            ariaLabel="요청 기한"
                                          />
                                        </span>
                                        <span>
                                          대상:{" "}
                                          <InlineText
                                            value={req.target}
                                            onCommit={(next) =>
                                              updateWeeklyDraft(selectedDate, (d) => updateDraftRequestLine(d, req.id, { target: next }))
                                            }
                                            placeholder="대상 부서"
                                            ariaLabel="요청 대상"
                                          />
                                        </span>
                                        <InlinePopoverBadge<WeeklyRequestLine["status"]>
                                          value={req.status}
                                          options={[
                                            { value: "pending", label: "승인대기", tone: "watch" },
                                            { value: "reviewing", label: "검토중", tone: "neutral" },
                                            { value: "approved", label: "승인", tone: "neutral" },
                                            { value: "overdue", label: "기한 초과", tone: "watch" },
                                          ]}
                                          onChange={(nextStatus) =>
                                            updateWeeklyDraft(selectedDate, (d) => updateDraftRequestLine(d, req.id, { status: nextStatus }))
                                          }
                                          ariaLabel="요청 상태"
                                        />
                                      </div>
                                      <div className={styles.quiet}>
                                        <InlineText
                                          value={req.impact}
                                          onCommit={(next) =>
                                            updateWeeklyDraft(selectedDate, (d) => updateDraftRequestLine(d, req.id, { impact: next }))
                                          }
                                          placeholder="—"
                                          ariaLabel="영향도"
                                        />
                                      </div>
                                    </div>
                                  ))
                                )}
                                <InlineGhostRow
                                  label="요청사항 추가"
                                  onClick={() => {
                                    updateWeeklyDraft(selectedDate, (d) =>
                                      addManualDraftLine(d, "requests", {
                                        text: "새 요청사항",
                                        secondary: "영향 검토",
                                        status: "pending",
                                      }),
                                    );
                                  }}
                                />
                                <SuggestionDrawer
                                  items={excluded.map((r) => ({ id: r.id, label: r.title, secondary: r.neededBy }))}
                                  onRestore={(id) => updateWeeklyDraft(selectedDate, (d) => includeDraftLine(d, "requests", id))}
                                  label="제외된 요청사항"
                                />
                              </div>
                            );
                          })()
                        ) : snapshot.requests.length === 0 ? (
                          <span className={styles.quiet}>—</span>
                        ) : (
                          snapshot.requests.map((req) => (
                            <div key={req.id} className={styles.requestItem}>
                              <div className={styles.requestTitle}>
                                {req.title}
                                {req.missingSource ? <MetaBadge danger>원본 없음</MetaBadge> : null}
                              </div>
                              <div className={styles.requestMeta}>
                                <span>기한: {req.neededBy}</span>
                                {req.target && req.target !== "—" ? <span>대상: {req.target}</span> : null}
                                <StatusWord tone={requestTone(req.status)}>{requestLabel(req.status)}</StatusWord>
                              </div>
                              <div className={styles.quiet}>
                                {req.impact}
                              </div>
                            </div>
                          ))
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            {/* 4. 업무 진행상황 (WBS) — 기본은 숨김 */}
            {effectiveSections.wbs ? (
              <section className={styles.section} aria-label="업무 진행상황">
                <div className={styles.sectionTitleBar}>
                  <h2 className={styles.sectionHeading}>
                    업무 진행상황
                    {!isDraft && !savedSections.wbs ? <MetaBadge>숨긴 값</MetaBadge> : null}
                  </h2>
                  <div className={styles.sectionTitleActions}>
                    <span className={styles.sectionHint}>
                      책임(A), 산출물, 핵심경로입니다. 책임이 없으면 담당을 적습니다. 기준선이 없으면 계획일은 비어 있습니다.
                    </span>
                  </div>
                </div>

                <div className={styles.scroll}>
                  <table className={styles.wbsTable}>
                    <colgroup>
                      <col style={{ width: "4%" }} />
                      <col style={{ width: "18%" }} />
                      <col style={{ width: "8%" }} />
                      <col style={{ width: "8%" }} />
                      <col style={{ width: "8%" }} />
                      <col style={{ width: "8%" }} />
                      <col style={{ width: "6%" }} />
                      <col style={{ width: "14%" }} />
                      <col style={{ width: "14%" }} />
                      <col style={{ width: "12%" }} />
                    </colgroup>
                    <thead>
                      <tr>
                        <th className={mergeClasses(styles.wbsTh, styles.headRule)} rowSpan={2} colSpan={2} scope="col">
                          작업
                        </th>
                        <th className={mergeClasses(styles.wbsTh, styles.wbsThGroup)} colSpan={2} scope="col">
                          계획
                        </th>
                        <th className={mergeClasses(styles.wbsTh, styles.wbsThGroup)} colSpan={2} scope="col">
                          실적
                        </th>
                        <th className={mergeClasses(styles.wbsTh, styles.num, styles.headRule)} rowSpan={2} scope="col">
                          공정률
                        </th>
                        <th className={mergeClasses(styles.wbsTh, styles.wbsThGroup)} scope="col">
                          책임(A)
                        </th>
                        <th className={mergeClasses(styles.wbsTh, styles.wbsThGroup)} scope="col">
                          산출물
                        </th>
                        <th className={mergeClasses(styles.wbsTh, styles.wbsThGroup)} scope="col">
                          핵심경로
                        </th>
                      </tr>
                      <tr>
                        <th className={mergeClasses(styles.wbsTh, styles.num, styles.headRule)} scope="col">착수일</th>
                        <th className={mergeClasses(styles.wbsTh, styles.num, styles.headRule)} scope="col">완료일</th>
                        <th className={mergeClasses(styles.wbsTh, styles.num, styles.headRule)} scope="col">착수일</th>
                        <th className={mergeClasses(styles.wbsTh, styles.num, styles.headRule)} scope="col">완료일</th>
                        <th className={mergeClasses(styles.wbsTh, styles.headRule)} scope="col">이름</th>
                        <th className={mergeClasses(styles.wbsTh, styles.headRule)} scope="col">산출물</th>
                        <th className={mergeClasses(styles.wbsTh, styles.headRule)} scope="col">해당 여부</th>
                      </tr>
                    </thead>
                    <tbody>
                      {snapshot.wbs.length === 0 ? (
                        <tr className={styles.activityRow}>
                          <td className={styles.plainName} colSpan={10}>
                            —
                          </td>
                        </tr>
                      ) : (
                        snapshot.wbs.map((row) => {
                          if (row.isPhase) {
                            return <PhaseRow key={row.id} row={row} styles={styles} />;
                          }
                          return <ActivityRow key={row.id} row={row} styles={styles} />;
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </section>
            ) : null}

            {/* 5. 이슈 및 리스크 추적 대장 */}
            <section className={mergeClasses(styles.section, "avoid-break")} aria-label="이슈 및 리스크 관리 대장">
              <div className={styles.sectionTitleBar}>
                <h2 className={styles.sectionHeading}>이슈 및 리스크 추적 대장</h2>
                <div className={styles.sectionTitleActions}>
                  <span className={styles.sectionHint}>
                    {snapshot.agingNote ?? "미결 이슈와 이번 주에 닫힌 이슈입니다."}
                  </span>
                </div>
              </div>


              <table className={styles.wbsTable}>
                <colgroup>
                  <col style={{ width: canEditDraft ? "8%" : "7%" }} />
                  <col style={{ width: canEditDraft ? "9%" : "8%" }} />
                  <col style={{ width: canEditDraft ? "22%" : "24%" }} />
                  <col style={{ width: canEditDraft ? "9%" : "6%" }} />
                  <col style={{ width: canEditDraft ? "11%" : "12%" }} />
                  <col style={{ width: canEditDraft ? "29%" : "35%" }} />
                  <col style={{ width: canEditDraft ? "8%" : "8%" }} />
                  {canEditDraft ? <col style={{ width: "4%" }} /> : null}
                </colgroup>
                <thead>
                  <tr>
                    <th className={mergeClasses(styles.wbsTh, styles.headRule)} scope="col">관리번호</th>
                    <th className={mergeClasses(styles.wbsTh, styles.num, styles.headRule)} scope="col">발생일</th>
                    <th className={mergeClasses(styles.wbsTh, styles.headRule)} scope="col">이슈 및 리스크명</th>
                    <th className={mergeClasses(styles.wbsTh, styles.center, styles.headRule)} scope="col">심각도</th>
                    <th className={mergeClasses(styles.wbsTh, styles.headRule)} scope="col">담당자 / 팀</th>
                    <th className={mergeClasses(styles.wbsTh, styles.headRule)} scope="col">금주 조치내용 및 향후 대책</th>
                    <th className={mergeClasses(styles.wbsTh, styles.center, styles.headRule)} scope="col">상태</th>
                    {canEditDraft ? <th className={mergeClasses(styles.wbsTh, styles.headRule)} data-print-hide=""></th> : null}
                  </tr>
                </thead>
                <tbody>
                  {canEditDraft && activeDraft ? (
                    (() => {
                      const included = activeDraft.issues.filter((i) => i.included !== false);
                      return (
                        <>
                          {included.length === 0 ? (
                            <tr className={styles.activityRow}>
                              <td className={styles.plainName} colSpan={8}>이슈 항목이 없습니다.</td>
                            </tr>
                          ) : (
                            included.map((iss, rowIdx) => {
                              const isLastRow = rowIdx === included.length - 1;
                              return (
                                <tr key={iss.id} className={styles.activityRow}>
                                  <td className={styles.actCellCode}>
                                    {iss.code}
                                  {iss.origin === "manual" || !iss.sourceId ? (
                                    <MetaBadge title="이 보고에만 있습니다">직접</MetaBadge>
                                  ) : iss.edited ? (
                                    <MetaBadge title="보고서에서 고쳤습니다">수정됨</MetaBadge>
                                  ) : null}
                                  </td>
                                  <td className={styles.actCellDate}>
                                    <InlineDatePopover
                                      value={iss.date}
                                      statusDate={snapshot.asOfDate}
                                      presets={[
                                        { label: "오늘", iso: selectedDate },
                                        { label: "어제", iso: addDays(selectedDate, -1) },
                                        { label: "지난주", iso: addDays(selectedDate, -7) },
                                      ]}
                                      onChange={(next) =>
                                        updateWeeklyDraft(selectedDate, (d) => updateDraftIssueLine(d, iss.id, { date: next }))
                                      }
                                      ariaLabel="발생일"
                                    />
                                  </td>
                                  <td className={styles.plainName}>
                                    <InlineText
                                      value={iss.title}
                                      isInitialEditing={iss.id === activeNewIssueId}
                                      onCommit={(next) => {
                                        if (activeNewIssueId === iss.id) setActiveNewIssueId(null);
                                        updateWeeklyDraft(selectedDate, (d) => updateDraftIssueLine(d, iss.id, { title: next }));
                                      }}
                                      ariaLabel="이슈명"
                                    />
                                    {iss.missingSource ? <MetaBadge danger>원본 없음</MetaBadge> : null}
                                  </td>
                                  <td className={styles.centerCell}>
                                  <InlinePopoverBadge<"High" | "Medium" | "Low" | null>
                                    value={iss.severity}
                                    renderBadge={() => <SeverityBadge severity={reportSeverity(iss.severity)} />}
                                    options={[
                                      { value: "High", label: "높음", icon: severityGlyph("high", 16) },
                                      { value: "Medium", label: "보통", icon: severityGlyph("medium", 16) },
                                      { value: "Low", label: "낮음", tone: "quiet", icon: severityGlyph("low", 16) },
                                      { value: null, label: "미정", icon: severityGlyph(undefined, 16) },
                                    ]}
                                      onChange={(nextSev) =>
                                        updateWeeklyDraft(selectedDate, (d) => updateDraftIssueLine(d, iss.id, { severity: nextSev }))
                                      }
                                      ariaLabel="심각도"
                                    />
                                  </td>
                                  <td className={styles.actCellDiscipline}>
                                    <InlineMemberPopover
                                      value={iss.owner}
                                      options={pmOptions.vendorOptions}
                                      onChange={(next) =>
                                        updateWeeklyDraft(selectedDate, (d) => updateDraftIssueLine(d, iss.id, { owner: next }))
                                      }
                                      placeholder="—"
                                      ariaLabel="담당자 / 팀"
                                    />
                                  </td>
                                  <td className={styles.actCellDiscipline}>
                                    <InlineText
                                      value={iss.action}
                                      onCommit={(next) =>
                                        updateWeeklyDraft(selectedDate, (d) => updateDraftIssueLine(d, iss.id, { action: next }))
                                      }
                                      ariaLabel="금주 조치내용"
                                    />
                                  </td>
                                  <td className={styles.centerCell}>
                                    <InlinePopoverBadge<WeeklyIssueLine["status"]>
                                      value={iss.status}
                                    options={[
                                      { value: "open", label: "미결", tone: "quiet" },
                                      { value: "inProgress", label: "조치중", tone: "neutral" },
                                      { value: "delayed", label: "지연", tone: "watch" },
                                      { value: "resolved", label: "해결완료", tone: "quiet" },
                                    ]}
                                      onChange={(nextStatus) =>
                                        updateWeeklyDraft(selectedDate, (d) => updateDraftIssueLine(d, iss.id, { status: nextStatus }))
                                      }
                                      ariaLabel="상태"
                                    />
                                  </td>
                                  <td className={styles.centerCell} data-print-hide="">
                                    <Menu positioning="below-end">
                                      <MenuTrigger disableButtonEnhancement>
                                        <button
                                          type="button"
                                          className={styles.rowMenuBtn}
                                          title="이슈 옵션 메뉴"
                                          aria-label="이슈 옵션 메뉴"
                                          onKeyDown={(e) => {
                                            if (isLastRow && e.key === "Tab" && !e.shiftKey) {
                                              e.preventDefault();
                                              handleAddNewIssue();
                                            }
                                          }}
                                        >
                                          <MoreHorizontal16Regular />
                                        </button>
                                      </MenuTrigger>
                                      <MenuPopover>
                                        <MenuList>
                                          {iss.origin === "manual" || !iss.sourceId ? (
                                            <>
                                              <MenuItem
                                                icon={<Add16Regular />}
                                                onClick={() => {
                                                  const recordId = useProjectStore.getState().addRecord("issue", {
                                                    title: iss.title,
                                                    severity: (iss.severity ? iss.severity.toLowerCase() : "medium") as any,
                                                    assignees: iss.owner && iss.owner !== "—" ? [iss.owner] : [],
                                                    sections: {
                                                      resolution: iss.action && iss.action !== "—" ? iss.action : "",
                                                      summary: iss.title,
                                                    },
                                                    status: iss.status === "resolved" ? "closed" : iss.status === "inProgress" ? "inProgress" : "open",
                                                  }, { open: false });
                                                  const newRec = useProjectStore.getState().project.records?.find((r) => r.id === recordId);
                                                  const code = newRec ? recordCode(newRec) : undefined;
                                                  updateWeeklyDraft(selectedDate, (d) =>
                                                    promoteManualIssueToRecord(d, iss.id, { id: recordId, code }),
                                                  );
                                                }}
                                              >
                                                프로젝트 대장으로 등록
                                              </MenuItem>
                                              <MenuItem
                                                icon={<Delete16Regular />}
                                                onClick={() => updateWeeklyDraft(selectedDate, (d) => deleteDraftLine(d, "issues", iss.id))}
                                              >
                                                완전 삭제
                                              </MenuItem>
                                            </>
                                          ) : (
                                            <MenuItem
                                              icon={<ArrowCounterclockwise16Regular />}
                                              onClick={() => updateWeeklyDraft(selectedDate, (d) => reloadSingleDraftLine(d, "issues", iss.id, project, resources))}
                                            >
                                              원본에서 다시 읽기
                                            </MenuItem>
                                          )}
                                          <MenuItem
                                            icon={<Delete16Regular />}
                                            onClick={() => updateWeeklyDraft(selectedDate, (d) => excludeDraftLine(d, "issues", iss.id))}
                                          >
                                            보고서에서 제외
                                          </MenuItem>
                                        </MenuList>
                                      </MenuPopover>
                                    </Menu>
                                  </td>
                                </tr>
                              );
                            })
                          )}
                          <InlineGhostRow
                            asTableRow
                            colSpan={8}
                            label="이슈 추가"
                            onClick={handleAddNewIssue}
                          />
                        </>
                      );
                    })()
                  ) : snapshot.issues.length === 0 ? (
                    <tr className={styles.activityRow}>
                      <td className={styles.plainName} colSpan={7}>—</td>
                    </tr>
                  ) : (
                    snapshot.issues.map((iss) => (
                      <tr key={iss.id} className={styles.activityRow}>
                        <td className={styles.actCellCode}>{iss.code}</td>
                        <td className={styles.actCellDate}>{iss.date}</td>
                        <td className={styles.plainName}>
                          {iss.title}
                          {iss.missingSource ? <MetaBadge danger>원본 없음</MetaBadge> : null}
                        </td>
                        <td className={styles.centerCell}>
                          <SeverityBadge severity={reportSeverity(iss.severity)} />
                        </td>
                        <td className={styles.actCellDiscipline}>{iss.owner}</td>
                        <td className={styles.actCellDiscipline}>{iss.action}</td>
                        <td className={styles.centerCell}>
                          <IssueStatusWord status={iss.status} />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>

              {canEditDraft && activeDraft ? (
                (() => {
                  const excluded = activeDraft.issues.filter((i) => i.included === false);
                  return (
                    <SuggestionDrawer
                      items={excluded.map((iss) => ({ id: iss.id, label: `${iss.code} ${iss.title}`, secondary: iss.action }))}
                      onRestore={(id) => updateWeeklyDraft(selectedDate, (d) => includeDraftLine(d, "issues", id))}
                      label="제외된 이슈 / 리스크 항목"
                    />
                  );
                })()
              ) : null}
            </section>

            {/* 6. 주차별 누적 공정 이력 — 기본은 표시 */}
            {effectiveSections.history ? (
              <section className={mergeClasses(styles.section, "avoid-break")} aria-label="주차별 공정 추이">
                <div className={styles.sectionTitleBar}>
                  <h2 className={styles.sectionHeading}>
                    주차별 누적 공정 이력
                    {!isDraft && !savedSections.history ? <MetaBadge>숨긴 값</MetaBadge> : null}
                  </h2>
                  <span className={styles.sectionHint}>줄을 누르면 그 주로 바뀝니다.</span>
                </div>

                <table className={styles.historyTable}>
                  <thead>
                    <tr>
                      <th className={styles.historyTh} scope="col">주차</th>
                      <th className={styles.historyTh} scope="col">보고 기간</th>
                      <th className={mergeClasses(styles.historyTh, styles.historyThNum)} scope="col">계획 공정</th>
                      <th className={mergeClasses(styles.historyTh, styles.historyThNum)} scope="col">실적 공정</th>
                      <th className={mergeClasses(styles.historyTh, styles.historyThNum)} scope="col">공정 차이</th>
                      <th className={mergeClasses(styles.historyTh, styles.historyThNum)} scope="col">미결 이슈</th>
                      <th className={mergeClasses(styles.historyTh, styles.historyThNum)} scope="col">해결 이슈</th>
                      <th className={mergeClasses(styles.historyTh, styles.historyThNum)} scope="col">요청 대기</th>
                      <th className={styles.historyTh} scope="col">평가</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.length === 0 ? (
                      <tr className={styles.activityRow}>
                        <td className={styles.plainName} colSpan={9}>
                          —
                        </td>
                      </tr>
                    ) : (
                      history.map((h) => {
                        const rowDate = h.statusDate ?? h.weekId;
                        const isSelected = rowDate === selectedDate;
                        return (
                          <tr
                            key={rowDate}
                            className={mergeClasses(styles.historyRow, isSelected && styles.historyRowSelected)}
                            tabIndex={0}
                            role="button"
                            aria-pressed={isSelected}
                            onClick={() => handleSelectWeek(rowDate)}
                            onKeyDown={(event) => {
                              if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault();
                                handleSelectWeek(rowDate);
                              }
                            }}
                          >
                            <td className={styles.historyTd}>
                              <span>{h.weekLabel}</span>
                              {h.isDraft ? (
                                <MetaBadge>초안</MetaBadge>
                              ) : null}
                            </td>
                            <td className={mergeClasses(styles.historyTd, styles.historyTdNum)}>{h.period}</td>
                            <td className={mergeClasses(styles.historyTd, styles.historyTdNum)}>
                              {h.hasBaseline === false ? "—" : `${h.planProgress.toFixed(1)}%`}
                            </td>
                            <td className={mergeClasses(styles.historyTd, styles.historyTdNum)}>
                              {h.actualProgress.toFixed(1)}%
                            </td>
                            <td className={mergeClasses(styles.historyTd, styles.historyTdNum)}>
                              {h.hasBaseline === false ? (
                                "—"
                              ) : (
                                <StatusWord tone={h.gap >= 0 ? "good" : "watch"}>
                                  {`${h.gap >= 0 ? "+" : ""}${h.gap.toFixed(1)}%p`}
                                </StatusWord>
                              )}
                            </td>
                            <td className={mergeClasses(styles.historyTd, styles.historyTdNum)}>{h.openIssues}건</td>
                            <td className={mergeClasses(styles.historyTd, styles.historyTdNum)}>{h.resolvedIssues}건</td>
                            <td className={mergeClasses(styles.historyTd, styles.historyTdNum)}>{h.pendingRequests}건</td>
                            <td className={styles.historyTd}>
                              <span className={styles.verdict}>
                                <StatusWord tone={verdictTone(h.status)}>{verdictLabel(h.status)}</StatusWord>
                                <span className={styles.quiet}>{h.statusLabel}</span>
                              </span>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </section>
            ) : null}
          </article>
          )}
        </div>
      </div>

      {/* 마감 확인창 */}
      <ConfirmDialog
        open={confirmCloseState.open}
        title="이번 주를 마감할까요?"
        message="마감하면 이 문서가 그 주의 기록이 됩니다."
        confirmLabel="마감"
        onCancel={() => setConfirmCloseState({ open: false, exportPdfAfter: false })}
        onConfirm={() => void handleConfirmClose()}
      />

      {/* 초안 버리기 확인 */}
      <ConfirmDialog
        open={confirmDiscard}
        title="초안을 버릴까요?"
        message={
          activeDraft?.isRevision
            ? "정정 중인 내용을 지우고 직전 마감본으로 돌아갑니다."
            : "이 초안을 지웁니다. 되돌릴 수 없습니다."
        }
        confirmLabel="버리기"
        onCancel={() => setConfirmDiscard(false)}
        onConfirm={handleDiscard}
      />

      <WeeklyCreateDialog
        open={createOpen}
        defaultDate={createDefaultDate}
        entries={entries}
        project={project}
        resources={resources}
        onClose={() => setCreateOpen(false)}
        onCreate={handleCreate}
      />

      {canEditDraft && activeDraft ? (
        <WeeklyEditDialog
          open={editOpen}
          onClose={() => setEditOpen(false)}
          styles={styles}
          draft={activeDraft}
          project={project}
          resources={resources}
          pmOptions={pmOptions}
          tab={editTab}
          onTab={setEditTab}
          onUpdateDraft={(updater) => updateWeeklyDraft(selectedDate, updater)}
        />
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------------------------------------
 * 시트 내부 인라인 편집 및 목록 관리 컴포넌트
 * ---------------------------------------------------------------------------------------------- */

function DraftTitleInput({
  value,
  fallback,
  className,
  onCommit,
}: {
  value: string;
  fallback: string;
  className: string;
  onCommit: (next: string) => void;
}) {
  const [text, setText] = useState(value);
  useEffect(() => {
    setText(value);
  }, [value]);

  const finish = () => {
    const trimmed = text.trim();
    const resolved = trimmed || fallback;
    setText(resolved);
    if (resolved !== value) onCommit(resolved);
  };

  return (
    <input
      type="text"
      aria-label="보고서 제목"
      className={className}
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={finish}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          (e.currentTarget as HTMLInputElement).blur();
        }
      }}
    />
  );
}

function InlineEditableText({
  value,
  placeholder,
  ariaLabel,
  className,
  onCommit,
}: {
  value: string;
  placeholder?: string;
  ariaLabel: string;
  className: string;
  onCommit: (next: string) => void;
}) {
  const [text, setText] = useState(value);
  useEffect(() => {
    setText(value);
  }, [value]);

  const finish = () => {
    if (text !== value) onCommit(text.trim());
  };

  return (
    <input
      type="text"
      aria-label={ariaLabel}
      placeholder={placeholder}
      className={className}
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={finish}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          (e.currentTarget as HTMLInputElement).blur();
        }
      }}
    />
  );
}

function WeekMemoInput({
  value,
  className,
  onCommit,
}: {
  value: string;
  className: string;
  onCommit: (next: string) => void;
}) {
  const [text, setText] = useState(value);
  useEffect(() => {
    setText(value);
  }, [value]);

  return (
    <textarea
      aria-label="평가 메모"
      placeholder="이번 주 평가를 적습니다."
      className={className}
      value={text}
      rows={3}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => {
        const next = text.trim();
        if (next !== value) onCommit(next);
      }}
    />
  );
}

function TaskSummaryCell({
  styles,
  lines,
  fallbackStrings,
  emptyText,
  emptyQuiet = false,
  isEditable = false,
  draftLines,
  onUpdateLine,
  onExcludeLine,
  onAddLine,
  onRestoreLine,
  onDeleteLine,
  onMoveLine,
}: {
  styles: ReturnType<typeof useStyles>;
  lines?: WeeklyTaskLine[];
  fallbackStrings: string[];
  emptyText: string;
  emptyQuiet?: boolean;
  isEditable?: boolean;
  draftLines?: WeeklyTaskLine[];
  onUpdateLine?: (lineId: string, text: string) => void;
  onExcludeLine?: (lineId: string) => void;
  onAddLine?: (afterId?: string, newId?: string) => void;
  onRestoreLine?: (lineId: string) => void;
  onDeleteLine?: (lineId: string) => void;
  onMoveLine?: (lineId: string, direction: -1 | 1) => void;
}) {
  const [activeNewId, setActiveNewId] = useState<string | null>(null);

  const handleAddLine = (afterId?: string) => {
    const nextId = newId("wline");
    setActiveNewId(nextId);
    onAddLine?.(afterId, nextId);
  };

  const allLines = isEditable && draftLines ? draftLines : lines;
  const items: WeeklyTaskLine[] = useMemo(() => {
    if (allLines && allLines.length > 0) {
      return [...allLines.filter((l) => l.included !== false)].sort((a, b) => a.order - b.order);
    }
    return fallbackStrings.map((text, idx) => ({
      id: `fb_${idx}`,
      text,
      included: true,
      order: idx,
      origin: "auto" as const,
    }));
  }, [allLines, fallbackStrings]);

  const excludedItems: WeeklyTaskLine[] = useMemo(() => {
    if (!isEditable || !allLines) return [];
    return allLines.filter((l) => l.included === false);
  }, [isEditable, allLines]);

  return (
    <div>
      {items.length === 0 ? (
        <span className={emptyQuiet ? styles.quiet : styles.summaryEmpty}>{emptyText}</span>
      ) : (
        <ul className={styles.bulletList}>
          {items.map((item, idx) => (
            <li key={item.id} className={styles.bulletItem}>
              {isEditable ? (
                <div className={styles.bulletRow}>
                  <span className={styles.bulletBullet}>•</span>
                  <div className={styles.bulletContent}>
                    {item.origin === "manual" ? <MetaBadge lead title="이 보고에만 있습니다">직접</MetaBadge> : null}
                    <InlineText
                      value={item.text}
                      isInitialEditing={item.id === activeNewId}
                      onCommit={(next) => {
                        if (activeNewId === item.id) setActiveNewId(null);
                        const trimmed = next.trim();
                        if (!trimmed && item.origin === "manual") {
                          onDeleteLine ? onDeleteLine(item.id) : onExcludeLine?.(item.id);
                        } else {
                          onUpdateLine?.(item.id, next);
                        }
                      }}
                      onEmptyEnter={() => {
                        if (activeNewId === item.id) setActiveNewId(null);
                        if (item.origin === "manual") {
                          onDeleteLine ? onDeleteLine(item.id) : onExcludeLine?.(item.id);
                        }
                      }}
                      onCancel={() => {
                        if (activeNewId === item.id) setActiveNewId(null);
                        if (!item.text.trim() && item.origin === "manual") {
                          onDeleteLine ? onDeleteLine(item.id) : onExcludeLine?.(item.id);
                        }
                      }}
                      onEnterNext={() => {
                        handleAddLine(item.id);
                      }}
                      placeholder="—"
                      ariaLabel="업무 항목"
                    />
                    {item.missingSource ? <MetaBadge danger>원본 없음</MetaBadge> : null}
                  </div>
                  <div className={`${styles.bulletActions} bullet-actions`} data-print-hide="">
                    {onMoveLine && items.length > 1 ? (
                      <>
                        <button
                          type="button"
                          className={styles.bulletActionBtn}
                          disabled={idx === 0}
                          onClick={() => onMoveLine(item.id, -1)}
                          title="위로 이동"
                          aria-label="위로 이동"
                          style={{ opacity: idx === 0 ? 0.3 : 1 }}
                        >
                          <ArrowUp16Regular style={{ fontSize: "12px" }} />
                        </button>
                        <button
                          type="button"
                          className={styles.bulletActionBtn}
                          disabled={idx === items.length - 1}
                          onClick={() => onMoveLine(item.id, 1)}
                          title="아래로 이동"
                          aria-label="아래로 이동"
                          style={{ opacity: idx === items.length - 1 ? 0.3 : 1 }}
                        >
                          <ArrowDown16Regular style={{ fontSize: "12px" }} />
                        </button>
                      </>
                    ) : null}
                    {onExcludeLine ? (
                      <button
                        type="button"
                        className={styles.bulletActionBtn}
                        onClick={() => onExcludeLine(item.id)}
                        aria-label="보고서에서 제외"
                      >
                        <Dismiss12Regular />
                      </button>
                    ) : null}
                  </div>
                </div>
              ) : (
                <>
                  {item.text}
                  {item.missingSource ? <MetaBadge danger>원본 없음</MetaBadge> : null}
                </>
              )}
            </li>
          ))}
        </ul>
      )}

      {isEditable ? (
        <>
          <InlineGhostRow
            label="작업 추가"
            onClick={() => handleAddLine()}
          />
          <SuggestionDrawer
            items={excludedItems.map((ex) => ({ id: ex.id, label: ex.text }))}
            onRestore={(id) => onRestoreLine?.(id)}
            label="제외된 제안 항목"
          />
        </>
      ) : null}
    </div>
  );
}

function originLabel(origin: WeeklyLineOrigin | undefined): string | null {
  if (origin === "prev") return "지난 주";
  if (origin === "manual") return "직접 입력";
  if (origin === "reloaded") return "새로 생김";
  return null;
}

function WeeklyListManager({
  styles,
  draft,
  project,
  resources,
  activeKey,
  onUpdateDraft
}: {
  styles: ReturnType<typeof useStyles>;
  draft: WeeklyReportDraft;
  project: ReturnType<typeof useProjectStore.getState>["project"];
  resources: ReturnType<typeof useCatalogStore.getState>["resources"];
  activeKey: Exclude<WeeklyListKey, "wbs">;
  onUpdateDraft: (updater: (d: WeeklyReportDraft) => WeeklyReportDraft) => void;
}) {
  const [suggestionQuery, setSuggestionQuery] = useState("");
  const [showManualAdd, setShowManualAdd] = useState(false);
  const [manualPrimary, setManualPrimary] = useState("");
  const [manualSecondary, setManualSecondary] = useState("");
  const [showPastPicker, setShowPastPicker] = useState(false);
  const [pastQuery, setPastQuery] = useState("");
  const [dragId, setDragId] = useState<string | null>(null);
  const layer = useDialogLayer();
  const menuProps = {
    mountNode: layer,
    positioning: { strategy: "fixed" as const, position: "below" as const, align: "end" as const },
  };

  useEffect(() => {
    setSuggestionQuery("");
    setShowManualAdd(false);
    setManualPrimary("");
    setManualSecondary("");
    setShowPastPicker(false);
    setPastQuery("");
  }, [activeKey]);

  const rawList = draft[activeKey] as Array<
    WeeklyTaskLine | WeeklyAgendaLine | WeeklyIssueLine | WeeklyRequestLine
  >;

  const includedRows = useMemo(
    () => [...rawList.filter((item) => item.included)].sort((a, b) => a.order - b.order),
    [rawList],
  );
  const suggestionRows = useMemo(
    () => [...rawList.filter((item) => !item.included)].sort((a, b) => a.order - b.order),
    [rawList],
  );

  const filteredSuggestions = useMemo(() => {
    if (suggestionRows.length <= 8 || !suggestionQuery.trim()) return suggestionRows;
    const q = suggestionQuery.trim().toLowerCase();
    return suggestionRows.filter((item) => {
      const text = "text" in item ? item.text : item.title;
      return text.toLowerCase().includes(q);
    });
  }, [suggestionRows, suggestionQuery]);

  const carryOverPack = useMemo(
    () => collectCarryOverCandidates(project, resources, draft.statusDate),
    [project, resources, draft.statusDate],
  );
  const hasPrevClosed = !!carryOverPack;

  const pastItems = useMemo(() => collectPastItems(project, activeKey), [project, activeKey]);
  const filteredPastItems = useMemo(() => {
    if (!pastQuery.trim()) return pastItems;
    const q = pastQuery.trim().toLowerCase();
    return pastItems.filter(
      (entry) =>
        entry.primaryText.toLowerCase().includes(q) ||
        (entry.secondaryText && entry.secondaryText.toLowerCase().includes(q)),
    );
  }, [pastItems, pastQuery]);

  const needsSecondaryInput = activeKey === "issues" || activeKey === "requests";

  const commitManualAdd = () => {
    const primary = manualPrimary.trim();
    if (!primary) {
      setShowManualAdd(false);
      return;
    }
    onUpdateDraft((d) =>
      addManualDraftLine(d, activeKey, {
        text: primary,
        secondary: needsSecondaryInput ? manualSecondary.trim() || "—" : undefined,
      }),
    );
    setManualPrimary("");
    setManualSecondary("");
    setShowManualAdd(false);
  };

  const handleOpenOriginal = (sourceId: string) => {
    if (activeKey === "issues" || activeKey === "requests") {
      useProjectStore.getState().setRecordFocus(sourceId);
    } else {
      useProjectStore.getState().setSchedulePeek(sourceId);
    }
  };

  const handleDropOnRow = (targetId: string) => {
    if (!dragId || dragId === targetId) return;
    const ids = includedRows.map((r) => r.id);
    const fromIdx = ids.indexOf(dragId);
    const toIdx = ids.indexOf(targetId);
    if (fromIdx < 0 || toIdx < 0) return;
    ids.splice(fromIdx, 1);
    ids.splice(toIdx, 0, dragId);
    onUpdateDraft((d) => reorderDraftLines(d, activeKey, ids));
    setDragId(null);
  };

  return (
    <div className={styles.editStack} aria-label="목록 항목 관리">
      <EditSection
        styles={styles}
        title={`이번 보고 (${includedRows.length})`}
        hint="보고에 올릴 줄을 고르고 문장을 고칩니다."
      >
        <div className={styles.listManagerHeader}>
        <div className={styles.sectionTitleActions}>
          {activeKey === "issues" || activeKey === "requests" ? (
            <SheetButton type="button" onClick={() => onUpdateDraft((d) => reloadDraftRecords(d, project))}>
              <ArrowCounterclockwise16Regular />
              기록에서 다시 읽기
            </SheetButton>
          ) : (
            <SheetButton type="button" onClick={() => onUpdateDraft((d) => reloadDraftSchedule(d, project, resources))}>
              <ArrowCounterclockwise16Regular />
              일정에서 다시 읽기
            </SheetButton>
          )}

          {hasPrevClosed ? (
            <SheetButton
              type="button"
              onClick={() => onUpdateDraft((d) => applyCarryOverToDraft(d, project, resources, activeKey))}
            >
              지난 보고의 미결 가져오기
            </SheetButton>
          ) : null}

          <SheetButton type="button" onClick={() => setShowPastPicker((v) => !v)} active={showPastPicker}>
            <History16Regular />
            지난 항목
          </SheetButton>
        </div>
      </div>

      {/* 지난 항목 검색 */}
      {showPastPicker ? (
        <div className={styles.pastSearchBox}>
          <div className={styles.pileHeader}>
            <span className={styles.pileTitle}>지난 마감본 항목에서 복사</span>
            <FuiSearchBox
              placeholder="이름으로 지난 항목 검색"
              value={pastQuery}
              onChange={(_, data) => setPastQuery(data.value)}
              aria-label="지난 항목 검색"
            />
          </div>
          {filteredPastItems.length === 0 ? (
            <span className={styles.quiet}>지난 마감본에 올라간 항목이 없습니다.</span>
          ) : (
            <div className={styles.pastSearchResults}>
              {filteredPastItems.map((entry) => (
                <div key={entry.key} className={styles.pastSearchItem}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0 }}>
                    <MetaBadge>{entry.weekLabel}</MetaBadge>
                    <span>{entry.primaryText}</span>
                    {entry.secondaryText ? <span className={styles.quiet}>· {entry.secondaryText}</span> : null}
                  </div>
                  <SheetButton
                    type="button"
                    onClick={() => onUpdateDraft((d) => copyPastItemToDraft(d, project, entry))}
                  >
                    복사
                  </SheetButton>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : null}

        {includedRows.length === 0 ? (
          <div className={styles.pileRow}>
            <span className={styles.quiet}>
              {activeKey === "currentTasks" ? "이번 주 진행 작업이 없습니다." : "—"}
            </span>
          </div>
        ) : (
          <div className={styles.pileList}>
            {includedRows.map((row, idx) => {
              const tag = originLabel(row.origin);
              const isManual = !row.sourceId;
              return (
                <div
                  key={row.id}
                  className={styles.pileRow}
                  draggable
                  onDragStart={() => setDragId(row.id)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => handleDropOnRow(row.id)}
                >
                  <span className={styles.dragHandle} title="끌어 순서 바꾸기">
                    <ReOrderDotsVertical16Regular />
                  </span>

                  <div className={styles.pileRowBody}>
                    {(activeKey === "prevTasks" || activeKey === "currentTasks" || activeKey === "nextTasks") &&
                    "text" in row ? (
                      <div style={{ flexGrow: 1, minWidth: "200px" }}>
                        <InlineEditableText
                          ariaLabel="보고 줄 문장"
                          value={row.text}
                          className={styles.inlineTextInput}
                          onCommit={(val) =>
                            onUpdateDraft((d) => updateDraftTaskLine(d, activeKey, row.id, { text: val || row.text }))
                          }
                        />
                      </div>
                    ) : null}

                    {activeKey === "agendas" && "type" in row ? (
                      <>
                        <span className={styles.quiet} style={{ flexShrink: 0 }}>
                          {row.date} · {row.status === "done" ? "완료" : row.type} · {row.owner}
                        </span>
                        <div style={{ flexGrow: 1, minWidth: "180px" }}>
                          <InlineEditableText
                            ariaLabel="보고일정 이름"
                            value={row.title}
                            className={styles.inlineTextInput}
                            onCommit={(val) =>
                              onUpdateDraft((d) => updateDraftAgendaLine(d, row.id, { title: val || row.title }))
                            }
                          />
                        </div>
                      </>
                    ) : null}

                    {activeKey === "issues" && "action" in row ? (
                      <>
                        <span className={styles.quiet} style={{ flexShrink: 0 }}>
                          {row.code} · {row.date}
                        </span>
                        {isManual ? (
                          <div style={{ width: "180px" }}>
                            <InlineEditableText
                              ariaLabel="이슈 이름"
                              value={row.title}
                              className={styles.inlineTextInput}
                              onCommit={(val) =>
                                onUpdateDraft((d) => updateDraftIssueLine(d, row.id, { title: val || row.title }))
                              }
                            />
                          </div>
                        ) : (
                          <span>{row.title}</span>
                        )}
                        <div style={{ flexGrow: 1, minWidth: "200px" }}>
                          <InlineEditableText
                            ariaLabel="금주 조치내용"
                            value={row.action}
                            placeholder="금주 조치내용"
                            className={styles.inlineTextInput}
                            onCommit={(val) =>
                              onUpdateDraft((d) => updateDraftIssueLine(d, row.id, { action: val || "—" }))
                            }
                          />
                        </div>
                      </>
                    ) : null}

                    {activeKey === "requests" && "impact" in row ? (
                      <>
                        <span className={styles.quiet} style={{ flexShrink: 0 }}>
                          {row.code} · 기한 {row.neededBy}
                        </span>
                        {isManual ? (
                          <div style={{ width: "180px" }}>
                            <InlineEditableText
                              ariaLabel="요청 이름"
                              value={row.title}
                              className={styles.inlineTextInput}
                              onCommit={(val) =>
                                onUpdateDraft((d) => updateDraftRequestLine(d, row.id, { title: val || row.title }))
                              }
                            />
                          </div>
                        ) : (
                          <span>{row.title}</span>
                        )}
                        <div style={{ flexGrow: 1, minWidth: "200px" }}>
                          <InlineEditableText
                            ariaLabel="영향 문장"
                            value={row.impact}
                            placeholder="영향 문장"
                            className={styles.inlineTextInput}
                            onCommit={(val) =>
                              onUpdateDraft((d) => updateDraftRequestLine(d, row.id, { impact: val || "—" }))
                            }
                          />
                        </div>
                      </>
                    ) : null}

                    {tag ? <MetaBadge>{tag}</MetaBadge> : null}
                    {isManual && row.carryOver ? <MetaBadge>다음 주로 넘김</MetaBadge> : null}
                    {row.missingSource ? <MetaBadge danger>원본 없음</MetaBadge> : null}
                  </div>

                  <SheetButton
                    type="button"
                    disabled={idx === 0}
                    aria-label="위로 이동"
                    onClick={() => onUpdateDraft((d) => moveDraftLine(d, activeKey, row.id, -1))}
                  >
                    <ArrowUp16Regular />
                  </SheetButton>
                  <SheetButton
                    type="button"
                    disabled={idx === includedRows.length - 1}
                    aria-label="아래로 이동"
                    onClick={() => onUpdateDraft((d) => moveDraftLine(d, activeKey, row.id, 1))}
                  >
                    <ArrowDown16Regular />
                  </SheetButton>

                  <Menu {...menuProps}>
                    <MenuTrigger disableButtonEnhancement>
                      <SheetButton type="button" aria-label="줄 메뉴">
                        <MoreHorizontal16Regular />
                      </SheetButton>
                    </MenuTrigger>
                    <MenuPopover>
                      <MenuList>
                        <MenuItem onClick={() => onUpdateDraft((d) => excludeDraftLine(d, activeKey, row.id))}>
                          빼기
                        </MenuItem>
                        {row.sourceId && !row.missingSource ? (
                          <MenuItem onClick={() => handleOpenOriginal(row.sourceId!)}>원본 열기</MenuItem>
                        ) : null}
                        {row.sourceId ? (
                          <MenuItem
                            onClick={() =>
                              onUpdateDraft((d) => reloadSingleDraftLine(d, activeKey, row.id, project, resources))
                            }
                          >
                            이 줄만 다시 읽기
                          </MenuItem>
                        ) : null}
                        {isManual ? (
                          <MenuItem
                            onClick={() => {
                              const nextCarry = !row.carryOver;
                              onUpdateDraft((d) => {
                                if (activeKey === "prevTasks" || activeKey === "currentTasks" || activeKey === "nextTasks") {
                                  return updateDraftTaskLine(d, activeKey, row.id, { carryOver: nextCarry });
                                }
                                if (activeKey === "agendas") {
                                  return updateDraftAgendaLine(d, row.id, { carryOver: nextCarry });
                                }
                                if (activeKey === "issues") {
                                  return updateDraftIssueLine(d, row.id, { carryOver: nextCarry });
                                }
                                return updateDraftRequestLine(d, row.id, { carryOver: nextCarry });
                              });
                            }}
                          >
                            {row.carryOver ? "다음 주로 넘김 해제" : "다음 주로 넘김"}
                          </MenuItem>
                        ) : null}
                      </MenuList>
                    </MenuPopover>
                  </Menu>
                </div>
              );
            })}
          </div>
        )}

        {/* 직접 추가: 목록 아래 「줄 추가」. 저장 버튼 없이 칸을 떠나면 초안에 반영 */}
        {showManualAdd ? (
          <div
            className={styles.manualAddRow}
            onBlur={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
                commitManualAdd();
              }
            }}
          >
            <FuiInput
              autoFocus
              placeholder="이름 / 문장 입력"
              value={manualPrimary}
              onChange={(_, data) => setManualPrimary(data.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !needsSecondaryInput) {
                  e.preventDefault();
                  commitManualAdd();
                } else if (e.key === "Escape") {
                  setShowManualAdd(false);
                }
              }}
              aria-label="새 줄 이름"
            />
            {needsSecondaryInput ? (
              <FuiInput
                placeholder={activeKey === "issues" ? "금주 조치내용 입력" : "영향 문장 입력"}
                value={manualSecondary}
                onChange={(_, data) => setManualSecondary(data.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    commitManualAdd();
                  } else if (e.key === "Escape") {
                    setShowManualAdd(false);
                  }
                }}
                aria-label={activeKey === "issues" ? "새 줄 조치내용" : "새 줄 영향 문장"}
              />
            ) : null}
          </div>
        ) : (
          <div>
            <SheetButton type="button" onClick={() => setShowManualAdd(true)}>
              <Add16Regular />
              줄 추가
            </SheetButton>
          </div>
        )}
      </EditSection>

      {suggestionRows.length > 0 ? (
        <EditSection
          styles={styles}
          title={`제안 (${suggestionRows.length})`}
          hint="이번 보고에서 뺀 줄입니다."
          extra={
            suggestionRows.length > 8 ? (
              <FuiSearchBox
                placeholder="제안 검색"
                value={suggestionQuery}
                onChange={(_, data) => setSuggestionQuery(data.value)}
                aria-label="제안 검색"
              />
            ) : undefined
          }
        >
          <div className={styles.pileList}>
            {filteredSuggestions.map((row) => {
              const primary = "text" in row ? row.text : row.title;
              const secondary =
                "action" in row
                  ? row.action
                  : "impact" in row
                    ? row.impact
                    : "date" in row
                      ? `${row.date} · ${row.owner}`
                      : undefined;
              const tag = originLabel(row.origin) ?? "자동";
              return (
                <div key={row.id} className={styles.pileRow}>
                  <div className={styles.pileRowBody}>
                    <span>{primary}</span>
                    {secondary ? <span className={styles.quiet}>· {secondary}</span> : null}
                    <MetaBadge>{tag}</MetaBadge>
                    {row.missingSource ? <MetaBadge danger>원본 없음</MetaBadge> : null}
                  </div>
                  <SheetButton
                    type="button"
                    onClick={() => onUpdateDraft((d) => includeDraftLine(d, activeKey, row.id))}
                  >
                    올리기
                  </SheetButton>
                </div>
              );
            })}
          </div>
        </EditSection>
      ) : null}
    </div>
  );
}

function WbsListManager({
  styles,
  draft,
  project,
  resources,
  onUpdateDraft
}: {
  styles: ReturnType<typeof useStyles>;
  draft: WeeklyReportDraft;
  project: ReturnType<typeof useProjectStore.getState>["project"];
  resources: ReturnType<typeof useCatalogStore.getState>["resources"];
  onUpdateDraft: (updater: (d: WeeklyReportDraft) => WeeklyReportDraft) => void;
}) {
  const layer = useDialogLayer();
  const menuProps = {
    mountNode: layer,
    positioning: { strategy: "fixed" as const, position: "below" as const, align: "end" as const },
  };
  const [suggestionQuery, setSuggestionQuery] = useState("");
  const includedRows = useMemo(
    () => [...draft.wbs.filter((r) => r.included)].sort((a, b) => a.order - b.order),
    [draft.wbs],
  );
  const suggestionRows = useMemo(
    () => [...draft.wbs.filter((r) => !r.included)].sort((a, b) => a.order - b.order),
    [draft.wbs],
  );
  const filteredSuggestions = useMemo(() => {
    if (suggestionRows.length <= 8 || !suggestionQuery.trim()) return suggestionRows;
    const q = suggestionQuery.trim().toLowerCase();
    return suggestionRows.filter((r) => r.name.toLowerCase().includes(q) || r.code.toLowerCase().includes(q));
  }, [suggestionRows, suggestionQuery]);

  return (
    <div className={styles.editStack} aria-label="업무 진행상황 항목 관리">
      <EditSection
        styles={styles}
        title={`이번 보고 (${includedRows.length})`}
        hint="올릴 행을 고릅니다."
        extra={
          <SheetButton type="button" onClick={() => onUpdateDraft((d) => reloadDraftSchedule(d, project, resources))}>
            <ArrowCounterclockwise16Regular />
            일정에서 다시 읽기
          </SheetButton>
        }
      >
        {includedRows.length === 0 ? (
          <div className={styles.pileRow}>
            <span className={styles.quiet}>—</span>
          </div>
        ) : (
          <div className={styles.pileList}>
            {includedRows.map((row, idx) => (
              <div key={row.id} className={styles.pileRow}>
                <div className={styles.pileRowBody}>
                  <span className={styles.quiet}>{row.code}</span>
                  <span>{row.name}</span>
                  <span className={styles.quiet}>{row.progress}%</span>
                  {row.missingSource ? <MetaBadge danger>원본 없음</MetaBadge> : null}
                </div>
                <SheetButton
                  type="button"
                  disabled={idx === 0}
                  onClick={() => onUpdateDraft((d) => moveDraftLine(d, "wbs", row.id, -1))}
                 aria-label="위로">
                  <ArrowUp16Regular />
                </SheetButton>
                <SheetButton
                  type="button"
                  disabled={idx === includedRows.length - 1}
                  onClick={() => onUpdateDraft((d) => moveDraftLine(d, "wbs", row.id, 1))}
                 aria-label="아래로">
                  <ArrowDown16Regular />
                </SheetButton>
                <Menu {...menuProps}>
                  <MenuTrigger disableButtonEnhancement>
                    <SheetButton type="button" aria-label="WBS 줄 메뉴">
                      <MoreHorizontal16Regular />
                    </SheetButton>
                  </MenuTrigger>
                  <MenuPopover>
                    <MenuList>
                      <MenuItem onClick={() => onUpdateDraft((d) => excludeDraftLine(d, "wbs", row.id))}>빼기</MenuItem>
                      {!row.missingSource ? (
                        <MenuItem onClick={() => useProjectStore.getState().setSchedulePeek(row.sourceId ?? row.id)}>
                          원본 열기
                        </MenuItem>
                      ) : null}
                      <MenuItem
                        onClick={() =>
                          onUpdateDraft((d) => reloadSingleDraftLine(d, "wbs", row.id, project, resources))
                        }
                      >
                        이 줄만 다시 읽기
                      </MenuItem>
                    </MenuList>
                  </MenuPopover>
                </Menu>
              </div>
            ))}
          </div>
        )}
      </EditSection>

      {suggestionRows.length > 0 ? (
        <EditSection
          styles={styles}
          title={`제안 (${suggestionRows.length})`}
          hint="이번 보고에서 뺀 행입니다."
          extra={
            suggestionRows.length > 8 ? (
              <FuiSearchBox
                placeholder="WBS 제안 검색"
                value={suggestionQuery}
                onChange={(_, data) => setSuggestionQuery(data.value)}
                aria-label="WBS 제안 검색"
              />
            ) : undefined
          }
        >
          <div className={styles.pileList}>
            {filteredSuggestions.map((row) => (
              <div key={row.id} className={styles.pileRow}>
                <div className={styles.pileRowBody}>
                  <span className={styles.quiet}>{row.code}</span>
                  <span>{row.name}</span>
                  <span className={styles.quiet}>{row.progress}%</span>
                </div>
                <SheetButton
                  type="button"
                  onClick={() => onUpdateDraft((d) => includeDraftLine(d, "wbs", row.id))}
                >
                  올리기
                </SheetButton>
              </div>
            ))}
          </div>
        </EditSection>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------------------------------------
 * 표시 헬퍼 및 WBS / 상태 서브컴포넌트
 * ---------------------------------------------------------------------------------------------- */

/* ------------------------------------------------------------------------------------------------
 * 작성 · 편집 대화상자
 * ---------------------------------------------------------------------------------------------- */

function WeeklyCreateDialog({
  open,
  defaultDate,
  entries,
  project,
  resources,
  onClose,
  onCreate,
}: {
  open: boolean;
  defaultDate: ISODate;
  entries: WeeklyEntryMeta[];
  project: ReturnType<typeof useProjectStore.getState>["project"];
  resources: ReturnType<typeof useCatalogStore.getState>["resources"];
  onClose: () => void;
  onCreate: (date: ISODate, carryOver: boolean) => void;
}) {
  const [date, setDate] = useState<string>(defaultDate);
  const [carry, setCarry] = useState(true);
  useEffect(() => {
    if (!open) return;
    setDate(defaultDate);
    setCarry(true);
  }, [open, defaultDate]);

  const valid = isISODate(date);
  const entry = valid ? entries.find((e) => e.statusDate === date) : undefined;
  const hasDraft = entry?.state === "draft";
  const hasClosed = (entry?.versions.length ?? 0) > 0;
  const blocked = !valid || (hasClosed && !hasDraft);
  const hasCarry = useMemo(
    () => open && valid && !hasDraft && !hasClosed && !!collectCarryOverCandidates(project, resources, date as ISODate),
    [open, valid, hasDraft, hasClosed, project, resources, date],
  );

  const hint = !valid
    ? "기준일을 날짜로 입력하세요."
    : hasDraft
      ? "이 기준일의 초안이 이미 있습니다. 그 초안을 엽니다."
      : hasClosed
        ? "이 기준일에는 마감본이 있습니다. 고치려면 문서 메뉴의 「정정」을 쓰세요."
        : undefined;

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title="새 보고서"
      width="sm"
      footer={
        <DialogActionsBar
          onCancel={onClose}
          onConfirm={() => valid && onCreate(date as ISODate, carry && hasCarry)}
          confirmLabel={hasDraft ? "열기" : "작성"}
          disabled={blocked}
        />
      }
    >
      <DialogField label="기준일" hint={hint}>
        <FuiInput
          density="dialog"
          type="date"
          value={date}
          onChange={(_, data) => setDate(data.value)}
          aria-label="보고 기준일"
        />
      </DialogField>
      {hasCarry ? (
        <DialogCheckbox label="지난 보고의 미결 항목을 가져오기" checked={carry} onChange={setCarry} />
      ) : null}
    </AppDialog>
  );
}

function EditSection({
  styles,
  title,
  hint,
  extra,
  children,
}: {
  styles: ReturnType<typeof useStyles>;
  title: string;
  hint: string;
  extra?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className={styles.editCard}>
      <div className={extra ? styles.editCardHeadRow : undefined}>
        <div className={styles.editCardHead}>
          <div className={styles.editCardTitle}>{title}</div>
          <Caption1 className={styles.editCardSubtitle}>{hint}</Caption1>
        </div>
        {extra}
      </div>
      {children}
    </section>
  );
}

function WeeklyEditDialog({
  open,
  onClose,
  styles,
  draft,
  project,
  resources,
  pmOptions,
  tab,
  onTab,
  onUpdateDraft,
}: {
  open: boolean;
  onClose: () => void;
  styles: ReturnType<typeof useStyles>;
  draft: WeeklyReportDraft;
  project: ReturnType<typeof useProjectStore.getState>["project"];
  resources: ReturnType<typeof useCatalogStore.getState>["resources"];
  pmOptions: { clientOptions: string[]; vendorOptions: string[] };
  tab: EditTab;
  onTab: (tab: EditTab) => void;
  onUpdateDraft: (updater: (d: WeeklyReportDraft) => WeeklyReportDraft) => void;
}) {
  const navRef = useRef<HTMLElement | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const weekLabel = shortWeekLabel(draft.statusDate);
  const current = EDIT_NAV.find((item) => item.key === tab) ?? EDIT_NAV[0];

  useEffect(() => {
    if (!open) return;
    navRef.current?.querySelector<HTMLButtonElement>("[aria-current='page']")?.focus({ preventScroll: true });
  }, [open]);

  useEffect(() => {
    if (contentRef.current) contentRef.current.scrollTop = 0;
  }, [tab]);

  const onNavKey = (event: KeyboardEvent<HTMLElement>) => {
    const buttons = [...(navRef.current?.querySelectorAll<HTMLButtonElement>("[data-edit-item]") ?? [])];
    const index = buttons.indexOf(document.activeElement as HTMLButtonElement);
    if (index < 0) return;
    let next = -1;
    if (event.key === "ArrowDown") next = Math.min(buttons.length - 1, index + 1);
    else if (event.key === "ArrowUp") next = Math.max(0, index - 1);
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = buttons.length - 1;
    else return;
    event.preventDefault();
    buttons[next]?.focus();
    buttons[next]?.click();
  };

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title={`${weekLabel} 편집`}
      width="xl"
      height={640}
      chrome="corner"
      className="w-[min(1000px,calc(100vw-2rem))] max-w-[min(1000px,calc(100vw-2rem))] sm:max-w-[min(1000px,calc(100vw-2rem))] max-h-[calc(100dvh-48px)] p-0"
    >
      <div className={styles.editFrame}>
        <div className={styles.editShell}>
          <nav ref={navRef} className={styles.editNav} aria-label="편집할 구역" onKeyDown={onNavKey}>
            <div className={styles.editGroup}>
              <p className={styles.editGroupLabel} title={weekLabel}>
                {weekLabel}
              </p>
              {EDIT_NAV.map((item) => {
                const on = item.key === tab;
                const Icon = item.icon;
                return (
                  <button
                    key={item.key}
                    type="button"
                    className={mergeClasses(styles.editItem, on && styles.editItemOn)}
                    data-edit-item=""
                    aria-current={on ? "page" : undefined}
                    title={`${weekLabel} · ${item.label}`}
                    onClick={() => onTab(item.key)}
                  >
                    <span className={mergeClasses(styles.editIcon, on && styles.editIconOn)} aria-hidden>
                      <Icon />
                    </span>
                    <span className={styles.editItemLabel}>{item.label}</span>
                  </button>
                );
              })}
            </div>
          </nav>
          <div className={styles.editContent}>
            <PageHeaderTitle className={styles.editContentHead}>
              {weekLabel} · {current?.label ?? ""}
            </PageHeaderTitle>
            <div ref={contentRef} className={styles.editScroll} aria-label={current?.label ?? ""}>
              {tab === "cover" ? (
                <WeeklyCoverPanel
                  styles={styles}
                  draft={draft}
                  project={project}
                  resources={resources}
                  pmOptions={pmOptions}
                  onUpdateDraft={onUpdateDraft}
                />
              ) : tab === "wbs" ? (
                <WbsListManager
                  styles={styles}
                  draft={draft}
                  project={project}
                  resources={resources}
                  onUpdateDraft={onUpdateDraft}
                />
              ) : (
                <WeeklyListManager
                  styles={styles}
                  draft={draft}
                  project={project}
                  resources={resources}
                  activeKey={tab}
                  onUpdateDraft={onUpdateDraft}
                />
              )}
            </div>
          </div>
        </div>
        <footer className={styles.editFoot}>
          <span>고친 내용은 바로 초안에 반영됩니다.</span>
          <FuiButton density="dialog" appearance="primary" onClick={onClose}>
            완료
          </FuiButton>
        </footer>
      </div>
    </AppDialog>
  );
}

/** 표지 탭: 제목, 담당, 평가, 메모, 섹션 표시. 용지 칸에 흩어져 있던 입력이 모인다. */
function WeeklyCoverPanel({
  styles,
  draft,
  project,
  resources,
  pmOptions,
  onUpdateDraft,
}: {
  styles: ReturnType<typeof useStyles>;
  draft: WeeklyReportDraft;
  project: ReturnType<typeof useProjectStore.getState>["project"];
  resources: ReturnType<typeof useCatalogStore.getState>["resources"];
  pmOptions: { clientOptions: string[]; vendorOptions: string[] };
  onUpdateDraft: (updater: (d: WeeklyReportDraft) => WeeklyReportDraft) => void;
}) {
  const setWeeklySectionDefaults = useProjectStore((state) => state.setWeeklySectionDefaults);
  const [sectionScope, setSectionScope] = useState<"week" | "plan">("week");

  const stamp = (d: WeeklyReportDraft): WeeklyReportDraft => ({ ...d, updatedAt: new Date().toISOString() });
  const weekSections: WeeklySectionVisibility = draft.sections ?? DEFAULT_WEEKLY_SECTIONS;
  const planSections: WeeklySectionVisibility = project.weeklySectionDefaults ?? DEFAULT_WEEKLY_SECTIONS;
  const shown = sectionScope === "week" ? weekSections : planSections;

  const toggleSection = (key: keyof WeeklySectionVisibility, checked: boolean) => {
    if (sectionScope === "plan") {
      setWeeklySectionDefaults({ [key]: checked });
      return;
    }
    onUpdateDraft((d) => stamp({ ...d, sections: { ...d.sections, [key]: checked } }));
  };

  return (
    <div className={styles.editStack}>
      <EditSection styles={styles} title="제목" hint="표지에 찍히는 이름입니다.">
        <DraftTitleInput
          value={draft.title}
          fallback={suggestPeriodName(draft.statusDate)}
          className={styles.inlineTextInput}
          onCommit={(title) => onUpdateDraft((d) => stamp({ ...d, title }))}
        />
      </EditSection>

      <EditSection styles={styles} title="담당" hint="발주와 수행의 담당입니다.">
        <div className={styles.coverGrid}>
          <DialogField label="발주사 총괄 PM">
            <FuiDropdown
              density="dialog"
              aria-label="발주사 총괄 PM"
              value={draft.clientPm || "발주 역할이 없습니다."}
              options={draftSelectOptions(draft.clientPm, pmOptions.clientOptions)}
              onValueChange={(clientPm) => onUpdateDraft((d) => stamp({ ...d, clientPm }))}
            />
          </DialogField>
          <DialogField label="수행사 PM">
            <FuiDropdown
              density="dialog"
              aria-label="수행사 PM"
              value={draft.vendorPm || "—"}
              options={draftSelectOptions(draft.vendorPm, pmOptions.vendorOptions)}
              onValueChange={(vendorPm) => onUpdateDraft((d) => stamp({ ...d, vendorPm }))}
            />
          </DialogField>
        </div>
      </EditSection>

      <EditSection styles={styles} title="종합 공정" hint="계산된 판정을 고치거나 설명 문장을 바꿉니다.">
        <DialogField label="종합 공정 평가">
          <div className={styles.coverRow}>
            <div style={{ width: "160px", flexShrink: 0 }}>
              <FuiDropdown
                density="dialog"
                aria-label="종합 공정 평가"
                value={draft.statusVerdict}
                options={verdictOptions(draft.computedVerdict)}
                onValueChange={(next) => {
                  const nextVerdict = next as WeeklyReportDraft["statusVerdict"];
                  onUpdateDraft((d) =>
                    stamp({ ...d, statusVerdict: nextVerdict, verdictEdited: nextVerdict !== d.computedVerdict }),
                  );
                }}
              />
            </div>
            <SheetButton type="button" onClick={() => onUpdateDraft((d) => reloadDraftProgress(d, project, resources))}>
              <ArrowCounterclockwise16Regular />
              공정 다시 읽기
            </SheetButton>
          </div>
        </DialogField>
        <DialogField label="공정 설명 문장">
          <InlineEditableText
            ariaLabel="종합 공정 설명 문장"
            value={draft.statusSummary}
            placeholder="공정 설명 문장을 입력하세요"
            className={styles.inlineTextInput}
            onCommit={(val) =>
              onUpdateDraft((d) => stamp({ ...d, statusSummary: val || d.statusSummary, statusSummaryEdited: true }))
            }
          />
        </DialogField>
      </EditSection>

      <EditSection styles={styles} title="메모" hint="평가와 비용에 남기는 문장입니다.">
        <DialogField label="평가 메모">
          <WeekMemoInput
            value={draft.weekMemo ?? ""}
            className={mergeClasses(styles.inlineTextInput, styles.memoInput)}
            onCommit={(weekMemo) => onUpdateDraft((d) => stamp({ ...d, weekMemo }))}
          />
        </DialogField>
        <DialogField label="비용 메모">
          <InlineEditableText
            ariaLabel="비용 성과 한 줄 메모"
            value={draft.costNote ?? ""}
            placeholder="선택 — 예: 이번 주는 원가 입력이 없습니다."
            className={styles.inlineTextInput}
            onCommit={(val) => onUpdateDraft((d) => stamp({ ...d, costNote: val.trim() || undefined }))}
          />
        </DialogField>
      </EditSection>

      <EditSection styles={styles} title="포함할 구역" hint="이 주만 바꾸거나, 앞으로 만드는 주의 기본으로 둡니다.">
        <div className={styles.sectionPopover}>
          <SegmentedControl
            ariaLabel="섹션 적용 범위"
            value={sectionScope}
            onChange={(v) => setSectionScope(v as "week" | "plan")}
            options={[
              { value: "week", label: "이 주" },
              { value: "plan", label: "이 플랜의 기본" },
            ]}
          />
          <div className={styles.sectionCheckList}>
            <Checkbox label="표지, 종합 공정" checked disabled />
            <Checkbox label="주요 업무, 이슈 대장" checked disabled />
            <Checkbox
              label="비용 성과"
              checked={shown.cost}
              onChange={(_, data) => toggleSection("cost", !!data.checked)}
            />
            <Checkbox
              label="업무 진행상황"
              checked={shown.wbs}
              onChange={(_, data) => toggleSection("wbs", !!data.checked)}
            />
            <Checkbox
              label="주차별 이력"
              checked={shown.history}
              onChange={(_, data) => toggleSection("history", !!data.checked)}
            />
          </div>
        </div>
      </EditSection>
    </div>
  );
}

function draftSelectOptions(current: string, options: string[]) {
  const values = current && !options.includes(current) ? [current, ...options] : options;
  return values.map((value) => ({ value, label: value }));
}

function verdictOptions(computed: WeeklyReportDraft["statusVerdict"]) {
  return (["normal", "watch", "critical", "unknown"] as const).map((value) => ({
    value,
    label: `${verdictLabel(value)}${computed === value ? " (계산값)" : ""}`,
  }));
}

function weeklyClientPm(value: string): string {
  const text = value.trim();
  if (!text || text === "—") return "발주 역할이 없습니다.";
  return text;
}

function SummaryCols() {
  return (
    <colgroup>
      <col style={{ width: "33.333%" }} />
      <col style={{ width: "33.333%" }} />
      <col style={{ width: "33.333%" }} />
    </colgroup>
  );
}

function agendaMark(item: { status: string; type: string }): string {
  if (item.status === "done") return "완료";
  return item.type;
}

function agendaTone(item: { status: string; type: string }): "quiet" | "watch" | "neutral" {
  if (item.status === "done") return "quiet";
  if (item.type === "미룸") return "watch";
  return "neutral";
}

function reportSeverity(severity: WeeklyV2IssueItem["severity"]): RecordSeverity | undefined {
  if (severity === "High") return "high";
  if (severity === "Low") return "low";
  if (severity === "Medium") return "medium";
  return undefined;
}

function verdictLabel(verdict: "normal" | "watch" | "critical" | "unknown"): string {
  if (verdict === "critical") return "위험";
  if (verdict === "watch") return "주의";
  if (verdict === "unknown") return "판정 불가";
  return "정상";
}

function verdictTone(verdict: "normal" | "watch" | "critical" | "unknown"): "good" | "watch" | "risk" | "quiet" {
  if (verdict === "critical") return "risk";
  if (verdict === "watch") return "watch";
  if (verdict === "unknown") return "quiet";
  return "good";
}

function requestLabel(status: "pending" | "approved" | "reviewing" | "overdue"): string {
  if (status === "overdue") return "기한 초과";
  if (status === "reviewing") return "검토중";
  if (status === "approved") return "승인";
  return "승인대기";
}

function requestTone(status: "pending" | "approved" | "reviewing" | "overdue"): "watch" | "neutral" {
  if (status === "overdue" || status === "pending") return "watch";
  return "neutral";
}

function PhaseRow({
  row,
  styles,
}: {
  row: WeeklyV2WbsRow & { missingSource?: boolean };
  styles: ReturnType<typeof useStyles>;
}) {
  return (
    <tr className={styles.phaseRow}>
      <td className={styles.phaseCellCode}>{row.code}</td>
      <td className={styles.phaseCellName}>
        {row.name}
        {row.missingSource ? <MetaBadge danger>원본 없음</MetaBadge> : null}
      </td>
      <td className={styles.phaseCellDate}>{row.planStart}</td>
      <td className={styles.phaseCellDate}>{row.planEnd}</td>
      <td className={styles.phaseCellDate}>{row.actualStart}</td>
      <td className={styles.phaseCellDate}>{row.actualEnd}</td>
      <td className={styles.phaseCellProgress}>{row.progress}%</td>
      <td className={styles.phaseCellDiscipline}>{row.accountable && row.accountable !== "—" ? row.accountable : "—"}</td>
      <td className={styles.phaseCellDiscipline}>
        <DisciplineCell info={row.design} styles={styles} linked />
      </td>
      <td className={styles.phaseCellDiscipline}>
        <DisciplineCell info={row.dev} styles={styles} />
      </td>
    </tr>
  );
}

function ActivityRow({
  row,
  styles,
}: {
  row: WeeklyV2WbsRow & { missingSource?: boolean };
  styles: ReturnType<typeof useStyles>;
}) {
  return (
    <tr className={styles.activityRow}>
      <td className={styles.actCellCode}>{row.code}</td>
      <td className={styles.actCellName}>
        {row.name}
        {row.missingSource ? <MetaBadge danger>원본 없음</MetaBadge> : null}
      </td>
      <td className={styles.actCellDate}>{row.planStart}</td>
      <td className={styles.actCellDate}>{row.planEnd}</td>
      <td className={styles.actCellDate}>{row.actualStart}</td>
      <td className={styles.actCellDate}>{row.actualEnd}</td>
      <td className={styles.actCellProgress}>{row.progress}%</td>
      <td className={styles.actCellDiscipline}>{row.accountable && row.accountable !== "—" ? row.accountable : "—"}</td>
      <td className={styles.actCellDiscipline}>
        <DisciplineCell info={row.design} styles={styles} linked />
      </td>
      <td className={styles.actCellDiscipline}>
        <DisciplineCell info={row.dev} styles={styles} />
      </td>
    </tr>
  );
}

function DisciplineCell({
  info,
  styles,
  linked = false,
}: {
  info: WeeklyV2WbsRow["pm"];
  styles: ReturnType<typeof useStyles>;
  linked?: boolean;
}) {
  if (!info || (info.deliverable === "—" && info.task === "—")) {
    return <span className={styles.quiet}>—</span>;
  }
  const deliverable = info.deliverable && info.deliverable !== "—" ? info.deliverable : "";
  const task = info.task && info.task !== "—" ? info.task : "";
  const mark = linked && deliverable ? <DeliverableMark name={deliverable} styles={styles} /> : null;
  if (task && mark) {
    return (
      <span className={styles.disciplineBody}>
        <span>{task}</span>
        {mark}
      </span>
    );
  }
  if (mark) return mark;
  return <span>{task || deliverable}</span>;
}

function DeliverableMark({ name, styles }: { name: string; styles: ReturnType<typeof useStyles> }) {
  return (
    <span className={styles.deliverableLink}>
      <Document16Regular />
      <span className={styles.deliverableName}>{name}</span>
    </span>
  );
}

function IssueStatusWord({ status }: { status: WeeklyV2IssueItem["status"] }) {
  if (status === "resolved") return <StatusWord tone="quiet">해결완료</StatusWord>;
  if (status === "delayed") return <StatusWord tone="watch">지연</StatusWord>;
  if (status === "inProgress") return <StatusWord>조치중</StatusWord>;
  return <StatusWord tone="quiet">미결</StatusWord>;
}
